import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Upload,
  Search,
  Save,
  Check,
  X,
  FileText,
  Calculator,
  ExternalLink,
  RefreshCw,
  Zap,
  Square,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Package,
  Eye,
  EyeOff,
  Info,
  BookmarkPlus,
  ClipboardPaste,
  Clipboard,
  Copy,
  Plus,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Settings,
  Brain,
  CheckCircle,
  Rocket,
} from 'lucide-react';
import { PageHeader } from '@/components/admin';
import { Button, Badge, Card, Spinner, Table, Input, Modal } from '@/components/common';
import api, { gaeb, products as productsApi } from '@/utils/api';
import { toast } from 'sonner';

/**
 * SSE-Antworten vor dem Lesen pruefen. Ohne das liest der Stream-Reader eine
 * JSON-Fehlerantwort (z.B. 503 "KI-Funktionen der Kalkulation deaktiviert")
 * als leeren Stream und die Seite meldet still "fertig, keine Treffer".
 */
async function assertStreamOk(response: Response) {
  if (response.ok) return
  let message = `Serverfehler (${response.status})`
  try {
    const body = await response.json()
    if (body?.error) message = body.error
  } catch {
    // keine JSON-Antwort — Statusmeldung reicht
  }
  throw new Error(message)
}

const STATUS_CONFIG = {
  verified: { label: 'Geprüft', bg: 'bg-emerald-100', text: 'text-emerald-800' },
  omd_priced: { label: 'Bepreist (OMD)', bg: 'bg-green-100', text: 'text-green-800' },
  auto_matched: { label: 'Artikel zugewiesen', bg: 'bg-blue-100', text: 'text-blue-800' },
  manual: { label: 'Manuell zugewiesen', bg: 'bg-blue-100', text: 'text-blue-800' },
  manual_matched: { label: 'Manuell zugewiesen', bg: 'bg-blue-100', text: 'text-blue-800' },
  oci_matched: { label: 'OCI Match', bg: 'bg-teal-100', text: 'text-teal-800' },
  external_p94: { label: 'P94 Import', bg: 'bg-indigo-100', text: 'text-indigo-800' },
  pending: { label: 'Offen', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  not_found: { label: 'Nicht gefunden', bg: 'bg-red-100', text: 'text-red-800' },
  skip: { label: 'Übersprungen', bg: 'bg-gray-100', text: 'text-gray-800' },
  article_from_lv: { label: 'LV-Artikel', bg: 'bg-emerald-100', text: 'text-emerald-800' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'priced', label: 'Artikel zugewiesen' },
  { value: 'pending', label: 'Offen' },
  { value: 'not_found', label: 'Nicht gefunden' },
  { value: 'skip', label: 'Übersprungen' },
];

const MATCH_STATUS_OPTIONS = [
  { value: 'pending', label: 'Offen' },
  { value: 'auto_matched', label: 'Artikel zugewiesen' },
  { value: 'omd_priced', label: 'Bepreist (OMD)' },
  { value: 'manual', label: 'Manuell zugewiesen' },
  { value: 'not_found', label: 'Nicht gefunden' },
  { value: 'skip', label: 'Übersprungen' },
];

const POSKENNZ_CONFIG = {
  V: { label: 'Vortext', color: 'bg-slate-200 text-slate-700' },
  H: { label: 'Hinweis', color: 'bg-amber-100 text-amber-700' },
  A: { label: 'Ausführung', color: 'bg-blue-100 text-blue-700' },
  G: { label: 'Gruppe', color: 'bg-violet-100 text-violet-700' },
  T: { label: 'Titel', color: 'bg-violet-100 text-violet-700' },
};

const DATANORM_SEARCH_MODES = [
  { value: 'fts', label: 'Volltext' },
  { value: 'article', label: 'Artikelnr.' },
  { value: 'ean', label: 'EAN' },
];

const CATEGORY_BADGES = {
  cable:          { label: 'Kabel', color: 'bg-blue-100 text-blue-700' },
  tray:           { label: 'Trasse', color: 'bg-amber-100 text-amber-700' },
  switchgear_up:  { label: 'Schalter UP', color: 'bg-green-100 text-green-700' },
  switchgear_ap:  { label: 'Schalter AP', color: 'bg-emerald-100 text-emerald-700' },
  distribution:   { label: 'Verteiler', color: 'bg-violet-100 text-violet-700' },
  lighting:       { label: 'Leuchte', color: 'bg-yellow-100 text-yellow-700' },
  fitting:        { label: 'Kleinmat.', color: 'bg-gray-200 text-gray-600' },
  device:         { label: 'Gerät', color: 'bg-cyan-100 text-cyan-700' },
  service:        { label: 'Leistung', color: 'bg-gray-100 text-gray-500' },
  title:          { label: 'Titel', color: 'bg-gray-50 text-gray-400' },
  composite:      { label: 'Sammel', color: 'bg-purple-100 text-purple-700' },
  unknown:        { label: '?', color: 'bg-red-50 text-red-400' },
};

// v2 Kategorie-Badges (position_kategorie)
const V2_KATEGORIE_BADGES = {
  sammelposition: { label: 'S', fullLabel: 'Sammelposition', color: 'bg-purple-100 text-purple-700 border border-purple-300' },
  einzelartikel:  { label: 'E', fullLabel: 'Einzelartikel', color: 'bg-blue-100 text-blue-700 border border-blue-300' },
  arbeit:         { label: 'A', fullLabel: 'Reine Arbeit', color: 'bg-orange-100 text-orange-700 border border-orange-300' },
  system:         { label: 'Sys', fullLabel: 'Systemposition', color: 'bg-amber-100 text-amber-700 border border-amber-300' },
  pauschal:       { label: 'P', fullLabel: 'Pauschal', color: 'bg-gray-100 text-gray-600 border border-gray-300' },
  sonderfall:     { label: '!', fullLabel: 'Sonderfall', color: 'bg-red-50 text-red-600 border border-red-300' },
};

// Noise words to skip when extracting search terms from langtext
const NOISE_WORDS = new Set([
  'liefern', 'und', 'montieren', 'montage', 'inkl', 'inklusive', 'einschl',
  'einschließlich', 'nach', 'gem', 'gemäß', 'laut', 'lt', 'din', 'vde',
  'wie', 'vor', 'beschrieben', 'entsprechend', 'komplett', 'fertig',
  'verlegen', 'anschließen', 'setzen', 'einbauen', 'installieren', 'samt',
  'mit', 'ohne', 'für', 'als', 'der', 'die', 'das', 'den', 'dem', 'des',
  'ein', 'eine', 'einer', 'eines', 'von', 'vom', 'zum', 'zur', 'im', 'in',
  'auf', 'an', 'aus', 'bei', 'bis', 'über', 'unter', 'stk', 'stück', 'psch',
  'typ', 'oder', 'bzw', 'ggf', 'ca', 'fabrikat', 'hersteller', 'gleichwertig',
]);

// Attention patterns for "inkl." hints
const ATTENTION_PATTERNS = [
  { pattern: /inkl(?:usive|\.)\s+(?:Formstücke|Bögen|Abzweige|T-Stücke|Verbinder)/i,
    hint: 'Inkl. Formstücke — zusätzliche Artikel kalkulieren!' },
  { pattern: /einschl(?:ießlich|\.)\s+(?:Befestigung|Montage|Klein)/i,
    hint: 'Inkl. Befestigungsmaterial' },
  { pattern: /komplett\s+mit/i,
    hint: 'Komplett-Position — Einzelteile prüfen!' },
  { pattern: /(?:Stoßverbinder|Schrauben|Muttern|Dübel)\s+und/i,
    hint: 'Mehrere Kleinteile enthalten' },
  { pattern: /(?:samt|zuzüglich|nebst)\s+(?:Zubehör|Kleinmaterial)/i,
    hint: 'Zubehör/Kleinmaterial einkalkulieren' },
  { pattern: /(?:Rahmen|Abdeckung|Tragring)\s+.*(?:1-fach|2-fach|3-fach)/i,
    hint: 'Rahmen separat kalkulieren (oft eigener Artikel)' },
];

const detectAttentionHints = (langtext) => {
  if (!langtext) return [];
  return ATTENTION_PATTERNS
    .filter(p => p.pattern.test(langtext))
    .map(p => p.hint);
};

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const truncateText = (text, maxLen = 100) => {
  if (!text) return '-';
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen) + '...';
};

const calculateGP = (menge, prNetto, prBasis) => {
  if (menge == null || prNetto == null) return null;
  const basis = prBasis && prBasis > 0 ? prBasis : 1;
  return (menge * prNetto) / basis;
};

/** Check if a position is a preamble/title row (not a normal billable position) */
const isPreambleRow = (pos) => {
  if (pos.pos_kenn_z && pos.pos_kenn_z !== 'N') return true;
  // Also treat menge=0 with no price as preamble if it has text
  if (pos.menge === 0 && pos.pr_netto === 0 && pos.art_langtext && pos.art_langtext.length > 5) return true;
  return false;
};

/** Extract key search terms from a langtext string */
const extractSearchTerms = (text) => {
  if (!text) return '';
  // Take first meaningful words, skip noise
  const words = text
    .replace(/\n/g, ' ')
    .split(/[\s,;/()]+/)
    .map(w => w.replace(/[^a-zA-Z0-9äöüÄÖÜß.-]/g, ''))
    .filter(w => w.length >= 3 && !NOISE_WORDS.has(w.toLowerCase()));
  return words.slice(0, 5).join(' ');
};

/** Find ALL preamble texts that are hierarchically above this position (OZ prefix matching) */
const findContextPreambles = (positions, currentPos) => {
  if (!currentPos?.oz) return [];
  const posOz = currentPos.oz.replace(/\s+/g, '').replace(/\.+$/, '');

  return positions
    .filter(p => {
      if (!isPreambleRow(p)) return false;
      const pOz = p.oz.replace(/\s+/g, '').replace(/\.+$/, '');
      // Parent preamble: position's OZ starts with preamble's OZ
      return posOz.startsWith(pOz) && posOz !== pOz;
    })
    .sort((a, b) => {
      // Least specific first (shortest OZ = highest level)
      const aOz = a.oz.replace(/\s+/g, '').replace(/\.+$/, '');
      const bOz = b.oz.replace(/\s+/g, '').replace(/\.+$/, '');
      return aOz.length - bOz.length;
    });
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const PosKennZTag = ({ posKennZ }) => {
  const config = POSKENNZ_CONFIG[posKennZ];
  if (!config) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};


// ==================== KALKULATIONS-SETTINGS PANEL (BUG-007 + BUG-008) ====================
const KalkulationsSettingsPanel = ({ projectId, onSettingsChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bgkBeschreibung, setBgkBeschreibung] = useState('');
  const [bgkBetrag, setBgkBetrag] = useState('');

  useEffect(() => {
    if (!projectId) return;
    api.get(`/gaeb/projects/${projectId}/kalkulations-settings`)
      .then(res => {
        setSettings(res.data);
        if (onSettingsChange) onSettingsChange(res.data);
      })
      .catch(err => console.error('Settings load error:', err));
  }, [projectId]);

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      const res = await api.put(`/gaeb/projects/${projectId}/kalkulations-settings`, newSettings);
      setSettings(res.data.settings);
      if (onSettingsChange) onSettingsChange(res.data.settings);
    } catch (err) {
      console.error('Settings save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    const updated = { ...settings, [field]: parseFloat(value) || 0 };
    setSettings(updated);
    // Debounced auto-save
    clearTimeout(handleFieldChange._timer);
    handleFieldChange._timer = setTimeout(() => saveSettings(updated), 1500);
  };

  const handleAddBgk = () => {
    if (!bgkBeschreibung.trim() || !bgkBetrag) return;
    const updated = {
      ...settings,
      bgk_positionen: [...(settings.bgk_positionen || []), { beschreibung: bgkBeschreibung.trim(), betrag: parseFloat(bgkBetrag) || 0 }],
    };
    updated.bgk_gesamt = updated.bgk_positionen.reduce((s, p) => s + p.betrag, 0);
    setSettings(updated);
    setBgkBeschreibung('');
    setBgkBetrag('');
    saveSettings(updated);
  };

  const handleRemoveBgk = (idx) => {
    const updated = {
      ...settings,
      bgk_positionen: settings.bgk_positionen.filter((_, i) => i !== idx),
    };
    updated.bgk_gesamt = updated.bgk_positionen.reduce((s, p) => s + p.betrag, 0);
    setSettings(updated);
    saveSettings(updated);
  };

  if (!settings) return null;

  return (
    <div className="mb-4">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 transition"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-gray-800">Kalkulations-Einstellungen</span>
            <span className="text-sm text-gray-500">
              (Monteur: {settings.stundensatz_monteur}€/h, Aufschlag: {settings.aufschlag_material_prozent}%, BGK: {(settings.bgk_gesamt || 0).toFixed(0)}€)
            </span>
            {saving && <RefreshCw className="h-3 w-3 animate-spin text-amber-500" />}
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </button>
        {expanded && (
          <div className="px-4 py-3 space-y-4">
            {/* Stundensätze + Aufschlag */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stundensatz Monteur (€/h)</label>
                <input
                  type="number"
                  step="0.50"
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={settings.stundensatz_monteur}
                  onChange={(e) => handleFieldChange('stundensatz_monteur', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stundensatz Helfer (€/h)</label>
                <input
                  type="number"
                  step="0.50"
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={settings.stundensatz_helfer}
                  onChange={(e) => handleFieldChange('stundensatz_helfer', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material-Aufschlag (%)</label>
                <input
                  type="number"
                  step="1"
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={settings.aufschlag_material_prozent}
                  onChange={(e) => handleFieldChange('aufschlag_material_prozent', e.target.value)}
                />
              </div>
            </div>

            {/* BGK */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Baustellengemeinkosten (BGK)</label>
              {settings.bgk_positionen?.length > 0 && (
                <div className="space-y-1 mb-2">
                  {settings.bgk_positionen.map((pos, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="flex-1 text-gray-600">{pos.beschreibung}</span>
                      <span className="font-medium">{pos.betrag.toFixed(2)} €</span>
                      <button
                        className="text-red-400 hover:text-red-600"
                        onClick={() => handleRemoveBgk(idx)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 text-sm font-semibold border-t pt-1">
                    <span className="flex-1">Gesamt BGK</span>
                    <span>{(settings.bgk_gesamt || 0).toFixed(2)} €</span>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Bezeichnung (z.B. Baustrom)"
                  className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={bgkBeschreibung}
                  onChange={(e) => setBgkBeschreibung(e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Betrag €"
                  className="w-28 border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={bgkBetrag}
                  onChange={(e) => setBgkBetrag(e.target.value)}
                />
                <button
                  className="px-3 py-1.5 bg-amber-500 text-white rounded text-sm hover:bg-amber-600"
                  onClick={handleAddBgk}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const CompositeItemRow = ({ item, saving, onSave, onDelete, onVerifySingle, onCopy }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(item.pr_netto || '');
  const [artNr, setArtNr] = useState(item.obeta_article_number || '');
  const [descr, setDescr] = useState(item.description || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced Datanorm search for this composite item
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await gaeb.searchDatanormAdvanced(searchQuery, 'fts', 8);
        const results = res.data?.results || res.data || [];
        setSearchResults(Array.isArray(results) ? results : []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSaveInline = () => {
    onSave(item.id, {
      pr_netto: parseFloat(price) || 0,
      obeta_article_number: artNr || null,
      match_status: parseFloat(price) > 0 ? 'manual' : item.match_status,
    });
    setEditing(false);
  };

  const handleApplyResult = (result) => {
    const netPrice = result.netPrice ?? result.net_price;
    const priceBase = result.priceBase ?? result.price_base ?? 1;
    const perUnit = priceBase > 1 ? netPrice / priceBase : netPrice;
    const text1 = result.shortText1 ?? result.short_text_1 ?? '';
    const text2 = result.shortText2 ?? result.short_text_2 ?? '';

    const kurztext = [text1, text2].filter(Boolean).join(' ');
    onSave(item.id, {
      pr_netto: perUnit,
      pr_basis: 1,
      obeta_article_number: result.articleNumber ?? result.article_number,
      obeta_ean: result.ean || '',
      obeta_kurztext: kurztext,
      description: kurztext,
      match_status: 'manual',
    });
    setDescr(kurztext);
    setExpanded(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const isLlmSuggested = item.match_status === 'llm_suggested';
  const isAutoMapped = item.match_status === 'auto_mapped';
  const isArticleFromLv = item.match_status === 'article_from_lv';

  return (
    <div className={`border-b ${isLlmSuggested ? 'border-yellow-200 bg-yellow-50/50' : isArticleFromLv ? 'border-emerald-200 bg-emerald-50/50' : 'border-purple-100'}`}>
      {/* Main row */}
      <div className={`px-4 py-2 flex items-center gap-3 ${isLlmSuggested ? 'hover:bg-yellow-100/50' : isArticleFromLv ? 'hover:bg-emerald-100/50' : 'hover:bg-gray-50'}`}>
        <span className="text-xs text-gray-400 w-6 text-right shrink-0">{item.sort_order}.</span>
        <span className="text-sm font-medium w-16 shrink-0">
          {item.quantity} {item.unit}
        </span>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="input text-xs py-1 px-2 w-32"
                placeholder="Art.Nr."
                value={artNr}
                onChange={(e) => setArtNr(e.target.value)}
              />
              <input
                type="number"
                step="0.01"
                className="input text-xs py-1 px-2 w-24"
                placeholder="EP Netto"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              <button className="p-1 text-green-600 hover:text-green-800" onClick={handleSaveInline} disabled={saving} title="Speichern">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => setEditing(false)} title="Abbrechen">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                className="text-sm bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 w-full px-0 py-0.5 truncate"
                value={descr}
                onChange={(e) => setDescr(e.target.value)}
                onBlur={() => { if (descr !== item.description) onSave(item.id, { description: descr }); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              />
              <div className="flex items-center gap-1 flex-wrap">
                {item.article_number_lv && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{item.article_number_lv}</span>
                )}
                {isArticleFromLv && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800">LV-Artikel</span>
                )}
                {isLlmSuggested && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-200 text-yellow-800">KI-Vorschlag</span>
                )}
                {isAutoMapped && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Auto</span>
                )}
                {(item.match_status === 'auto_matched' || item.match_status === 'omd_priced') && item.match_source && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    item.match_source?.includes('festpreis') ? 'bg-amber-100 text-amber-700' :
                    item.match_source?.includes('wissenskatalog') ? 'bg-blue-100 text-blue-700' :
                    item.match_source?.includes('llm_experte') ? 'bg-purple-100 text-purple-700' :
                    item.match_source?.includes('datanorm_artnr') || item.match_source?.includes('datanorm_sku') ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {item.match_source?.includes('festpreis') ? '📌 Festpreis' :
                     item.match_source?.includes('wissenskatalog') ? '📚 Katalog' :
                     item.match_source?.includes('llm_experte') ? '🧠 LLM' :
                     item.match_source?.includes('datanorm_artnr') ? '🎯 Art.Nr.' :
                     item.match_source?.includes('datanorm_sku') ? '🔑 SKU' :
                     item.match_source?.includes('datanorm_fts') ? '🔍 FTS' :
                     item.match_source?.includes('mapping') ? '📎 Mapping' :
                     'Auto'}
                  </span>
                )}
                {item.match_status === 'omd_priced' && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                    ✅ OMD
                  </span>
                )}
                {item.obeta_article_number && !item.article_number_lv && (
                  <span className="text-[10px] text-gray-400 font-mono">{item.obeta_article_number}</span>
                )}
              </div>
              {(item.manufacturer || item.specs) && (
                <div className="text-[10px] text-gray-400">
                  {item.manufacturer && <span>Herst.: {item.manufacturer}</span>}
                  {item.specs && <span className="ml-2">({item.specs})</span>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!editing && (
            <button
              className={`text-sm font-medium ${item.pr_netto > 0 ? 'text-green-600' : 'text-gray-400'} hover:underline`}
              onClick={() => { setEditing(true); setPrice(item.pr_netto || ''); setArtNr(item.obeta_article_number || ''); }}
            >
              {item.pr_netto > 0 ? formatCurrency(item.pr_netto) : '-'}
            </button>
          )}
          <button onClick={() => {
            if (!expanded) {
              // Pre-fill search from notes (searchHint) or article_number_lv
              const hint = item.notes || item.article_number_lv || '';
              if (hint && !searchQuery) setSearchQuery(hint);
            }
            setExpanded(!expanded);
          }} title="Datanorm-Suche" className="p-1 hover:bg-blue-50 rounded">
            <Search className="w-3.5 h-3.5 text-blue-500" />
          </button>
          {item.obeta_article_number && item.pr_netto > 0 && item.match_status !== 'verified' && onVerifySingle && (
            <button onClick={() => onVerifySingle(item.id)} title="In Katalog übernehmen" className="p-1 hover:bg-emerald-50 rounded">
              <Plus className="w-3.5 h-3.5 text-emerald-500" />
            </button>
          )}
          {item.match_status === 'verified' && (
            <span className="text-emerald-500 text-xs px-1" title="Im Katalog">✓</span>
          )}
          {onCopy && (
            <button onClick={() => onCopy(item)} title="Artikel kopieren" className="p-1 hover:bg-amber-50 rounded">
              <Copy className="w-3.5 h-3.5 text-amber-500" />
            </button>
          )}
          <button onClick={() => onDelete(item.id)} title="Entfernen" className="p-1 hover:bg-red-50 rounded">
            <X className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      </div>

      {/* Expandable: Datanorm search for this item */}
      {expanded && (
        <div className="px-4 pb-3 ml-9">
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                className="input text-xs py-1.5 pl-8"
                placeholder="Datanorm-Suche..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searching && <RefreshCw className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 animate-spin" />}
            </div>
            <button
              className="text-xs text-orange-600 hover:text-orange-800 px-2 py-1 border border-orange-200 rounded hover:bg-orange-50"
              onClick={() => window.open(`https://www.obeta.de/search?query=${encodeURIComponent(searchQuery || item.description)}`, '_blank')}
              title="In Obeta suchen"
            >
              OCI
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded max-h-48 overflow-y-auto divide-y divide-gray-100">
              {searchResults.map((r, i) => {
                const artNr = r.articleNumber ?? r.article_number;
                const text1 = r.shortText1 ?? r.short_text_1 ?? '';
                const price = r.netPrice ?? r.net_price;
                const base = r.priceBase ?? r.price_base ?? 1;
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs hover:bg-blue-50">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-gray-700">{artNr}</span>
                      <span className="ml-2 text-gray-600 truncate">{text1}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="font-medium">{formatCurrency(price)}</span>
                      {base > 1 && <span className="text-[10px] text-gray-400">/{base}</span>}
                      <button className="text-blue-600 hover:text-blue-800 font-medium" onClick={() => handleApplyResult(r)}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <p className="text-[10px] text-gray-400 mt-1">Keine Ergebnisse</p>
          )}
        </div>
      )}
    </div>
  );
};

const KalkulationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Data state
  const [project, setProject] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Filter/Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPreambles, setShowPreambles] = useState(true);
  const [datanormQuery, setDatanormQuery] = useState('');
  const [datanormResults, setDatanormResults] = useState([]);
  const [datanormSearching, setDatanormSearching] = useState(false);
  const [showDatanormDropdown, setShowDatanormDropdown] = useState(false);

  // Edit Modal state
  const [editModal, setEditModal] = useState({ open: false, position: null });
  const [editForm, setEditForm] = useState({});
  const [modalDatanormQuery, setModalDatanormQuery] = useState('');
  const [modalDatanormMode, setModalDatanormMode] = useState('fts');
  const [modalDatanormResults, setModalDatanormResults] = useState([]);
  const [modalDatanormSearching, setModalDatanormSearching] = useState(false);

  // Composite items state
  const [compositeItems, setCompositeItems] = useState([]);
  // compositeExpanded removed — Einzelartikel section is always visible
  const [compositeLoading, setCompositeLoading] = useState(false);
  const [savingCompositeId, setSavingCompositeId] = useState(null);
  const [extracting, setExtracting] = useState(false);

  // Clipboard state (survives position navigation)
  const [clipboardItems, setClipboardItems] = useState([]);

  // Composite Template state
  const [templates, setTemplates] = useState([]);
  const [showTemplateSave, setShowTemplateSave] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [showExpertMenu, setShowExpertMenu] = useState(false);
  // Close expert menu on outside click
  useEffect(() => {
    if (!showExpertMenu) return;
    const close = (e) => { if (!e.target.closest('[data-expert-menu]')) setShowExpertMenu(false); };
    document.addEventListener('click', close, { capture: true });
    return () => document.removeEventListener('click', close, { capture: true });
  }, [showExpertMenu]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Import Modal state
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Export state
  const [exportingP94, setExportingP94] = useState(false);
  const [exportingP93, setExportingP93] = useState(false);

  // Auto-resolve state
  const [resolving, setResolving] = useState(false);
  const [resolveProgress, setResolveProgress] = useState({ current: 0, total: 0, position: '' });
  const [resolveResult, setResolveResult] = useState(null);
  const [resolveAbort, setResolveAbort] = useState(null);

  // OCI sidebar state
  const [ociSidebarItems, setOciSidebarItems] = useState([]);
  const [ociSidebarFilter, setOciSidebarFilter] = useState('');
  // activeArticleField removed — sidebar always adds as new composite item
  const [ociSidebarLoading, setOciSidebarLoading] = useState(false);

  // Catalog sidebar state
  const [sidebarTab, setSidebarTab] = useState('oci'); // 'oci' | 'catalog'
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Auto-save state
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'dirty' | 'error'
  const saveTimerRef = useRef(null);
  const handleSaveRef = useRef(null);

  // OMD pricing state
  const [omdRunning, setOmdRunning] = useState(false);
  const [omdProgress, setOmdProgress] = useState({ current: 0, total: 0 });

  // LLM Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState({ current: 0, total: 0 });
  const [categoryFilter, setCategoryFilter] = useState('');

  // Projektprofil v2 state
  const [projektProfil, setProjektProfil] = useState(null);
  const [profilLoading, setProfilLoading] = useState(false);
  const [profilExpanded, setProfilExpanded] = useState(true);
  const [kalkSettings, setKalkSettings] = useState(null);

  // v2 Positions-Auflösung state
  const [v2Resolving, setV2Resolving] = useState(false);
  const [v2ResolveProgress, setV2ResolveProgress] = useState({ current: 0, total: 0 });
  const [v2Searching, setV2Searching] = useState(false);
  const [v2SearchProgress, setV2SearchProgress] = useState({ current: 0, total: 0, found: 0, notFound: 0 });
  const [v2OmdRunning, setV2OmdRunning] = useState(false);
  const [v2OmdProgress, setV2OmdProgress] = useState({ current: 0, total: 0, verified: 0, failed: 0 });

  // v2 Auto-Resolve (Gesamt-Flow)
  const [v2AutoResolving, setV2AutoResolving] = useState(false);
  const [v2AutoPhase, setV2AutoPhase] = useState(0);
  const [v2AutoPhaseName, setV2AutoPhaseName] = useState('');
  const [v2AutoProgress, setV2AutoProgress] = useState({});
  const [v2GapsReport, setV2GapsReport] = useState(null);

  // Load project and positions
  const loadData = useCallback(async () => {
    try {
      const [projectRes, positionsRes] = await Promise.all([
        gaeb.getProject(id),
        gaeb.getPositions(id),
      ]);
      setProject(projectRes.data);
      setPositions(positionsRes.data || []);

      // Load project profile (v2) — wenn keins existiert, automatisch erstellen
      try {
        const profilRes = await gaeb.getProjectProfile(id);
        setProjektProfil(profilRes.data);
      } catch {
        // Kein Profil vorhanden → automatisch erstellen (im Hintergrund)
        setProjektProfil(null);
        console.log('[v2] Kein Projektprofil vorhanden, erstelle automatisch...');
        setProfilLoading(true);
        try {
          const res = await gaeb.createProjectProfile(id, { force: false });
          setProjektProfil(res.data?.profile || res.data);
          toast.success('Projektprofil automatisch erstellt');
        } catch (profileErr) {
          console.error('[v2] Auto-Profilerstellung fehlgeschlagen:', profileErr);
        } finally {
          setProfilLoading(false);
        }
      }
    } catch (err) {
      console.error('Failed to load project:', err);
      toast.error('Projekt nicht gefunden');
      navigate('/admin/kalkulation');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced datanorm search (header bar)
  useEffect(() => {
    if (!datanormQuery || datanormQuery.length < 2) {
      setDatanormResults([]);
      setShowDatanormDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setDatanormSearching(true);
      try {
        const res = await gaeb.searchDatanorm(datanormQuery, 10);
        setDatanormResults(res.data || []);
        setShowDatanormDropdown(true);
      } catch (err) {
        console.error('Datanorm search error:', err);
        setDatanormResults([]);
      } finally {
        setDatanormSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [datanormQuery]);

  // Debounced datanorm search (modal) — with mode support (C1)
  useEffect(() => {
    if (!modalDatanormQuery || modalDatanormQuery.length < 2) {
      setModalDatanormResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setModalDatanormSearching(true);
      try {
        const res = await gaeb.searchDatanormAdvanced(modalDatanormQuery, modalDatanormMode, 15);
        // The response comes as { results, count, elapsed, query, mode }
        const results = res.data?.results || res.data || [];
        setModalDatanormResults(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error('Datanorm search error:', err);
        setModalDatanormResults([]);
      } finally {
        setModalDatanormSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [modalDatanormQuery, modalDatanormMode]);

  // Close datanorm dropdown on outside click
  useEffect(() => {
    const handleClick = () => setShowDatanormDropdown(false);
    if (showDatanormDropdown) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showDatanormDropdown]);

  // Filter positions (A1: respect showPreambles toggle)
  const filteredPositions = useMemo(() => {
    return positions.filter((pos) => {
      // A1: Hide preamble rows when toggle is off
      if (!showPreambles && isPreambleRow(pos)) return false;

      // Text search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesText =
          (pos.oz && pos.oz.toLowerCase().includes(q)) ||
          (pos.art_langtext && pos.art_langtext.toLowerCase().includes(q)) ||
          (pos.obeta_article_number && pos.obeta_article_number.toLowerCase().includes(q));
        if (!matchesText) return false;
      }

      // Status filter (don't filter preamble rows by status)
      if (statusFilter && !isPreambleRow(pos)) {
        if (statusFilter === 'priced') {
          const pricedStatuses = ['auto_matched', 'manual', 'manual_matched', 'oci_matched', 'external_p94', 'omd_priced', 'verified'];
          if (!pricedStatuses.includes(pos.match_status)) return false;
        } else {
          if (pos.match_status !== statusFilter) return false;
        }
      }

      // Category filter (supports both v1 analysis_category and v2 position_kategorie)
      if (categoryFilter) {
        if (categoryFilter === '_geprüft') {
          if (!pos.manuell_geprüft) return false;
        } else if (categoryFilter === '_warnung') {
          const hasWarns = pos.warnungen && pos.warnungen !== '[]' && pos.warnungen !== 'null';
          if (!hasWarns) return false;
        } else if (categoryFilter === '_offen') {
          // Offene Items: Material-Composite-Items ohne Artikelnummer
          // Wird in v2 über Stücklisten bestimmt — hier approximieren über match_status
          if (pos.match_status !== 'pending' && pos.match_status !== 'not_found') return false;
        } else if (Object.keys(V2_KATEGORIE_BADGES).includes(categoryFilter)) {
          if (pos.position_kategorie !== categoryFilter) return false;
        } else {
          if (pos.analysis_category !== categoryFilter) return false;
        }
      }

      return true;
    });
  }, [positions, searchQuery, statusFilter, showPreambles, categoryFilter]);

  // Projektprofil erstellen/neu generieren (Kalkulation v2)
  const handleCreateProfile = async (force = false) => {
    setProfilLoading(true);
    try {
      const res = await gaeb.createProjectProfile(id, { force });
      setProjektProfil(res.data);
      toast.success(force ? 'Projektprofil neu generiert' : 'Projektprofil erstellt');
    } catch (err) {
      console.error('Profile creation error:', err);
      if (err.response?.status === 409 && !force) {
        // Profile exists, reload it
        try {
          const profilRes = await gaeb.getProjectProfile(id);
          setProjektProfil(profilRes.data);
        } catch { /* ignore */ }
        toast.error('Profil existiert bereits. "Neu generieren" verwenden.');
      } else if (err.response?.status === 504 || err.code === 'ECONNABORTED') {
        // Timeout — profile might still be created in background
        toast.error('Timeout — Profil wird im Hintergrund erstellt. Bitte Seite in 2 Min. neu laden.');
        // Try loading after delay
        setTimeout(async () => {
          try {
            const profilRes = await gaeb.getProjectProfile(id);
            setProjektProfil(profilRes.data);
            toast.success('Projektprofil wurde erfolgreich erstellt');
          } catch { /* still not ready */ }
        }, 30000);
      } else {
        toast.error(err.response?.data?.error || 'Fehler beim Erstellen des Projektprofils');
      }
    } finally {
      setProfilLoading(false);
    }
  };

  // v2: Positionen auflösen (Stücklisten generieren via LLM)
  const handleResolvePositions = async () => {
    setV2Resolving(true);
    setV2ResolveProgress({ current: 0, total: 0 });

    try {
      const hasExisting = positions.some(p => p.position_kategorie);
      const skipExisting = hasExisting
        ? !window.confirm('Bereits aufgelöste Positionen erneut auflösen?')
        : true;

      const response = await fetch(`/api/gaeb/projects/${id}/resolve-positions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipExisting }),
        credentials: 'include',
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.substring(6));
            if (event.type === 'resolve_start') {
              setV2ResolveProgress({ current: 0, total: event.total });
            } else if (event.type === 'resolve_progress') {
              setV2ResolveProgress({ current: event.current, total: event.total });
            } else if (event.type === 'resolve_done' || event.type === 'complete') {
              toast.success(`${event.resolved || 0} Positionen aufgelöst — $${(event.totalCost || 0).toFixed(2)}`);
            } else if (event.type === 'error') {
              toast.error(event.error || 'Fehler bei der Auflösung');
            }
          } catch { /* ignore parse errors */ }
        }
      }

      // Reload data
      await loadData();
    } catch (err) {
      console.error('Resolve positions error:', err);
      toast.error(err instanceof Error && err.message ? err.message : 'Fehler beim Auflösen der Positionen');
    } finally {
      setV2Resolving(false);
    }
  };

  // v2 Phase 3 + 3.5: Artikelsuche (Wissenskatalog + Datanorm-FTS + optional LLM-Experte)
  const handleSearchArticles = async (useLLM = false) => {
    setV2Searching(true);
    setV2SearchProgress({ current: 0, total: 0, found: 0, notFound: 0, phase: 'fts', llmFound: 0 });

    try {
      const response = await fetch(`/api/gaeb/projects/${id}/search-articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ useLLM }),
        credentials: 'include',
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.substring(6));
            if (event.type === 'search_start') {
              setV2SearchProgress(p => ({ ...p, total: event.total, phase: 'fts' }));
            } else if (event.type === 'search_progress') {
              setV2SearchProgress(p => ({
                ...p,
                current: event.current,
                total: event.total,
                found: event.found,
                notFound: event.notFound,
              }));
            } else if (event.type === 'search_done') {
              setV2SearchProgress(p => ({
                ...p,
                found: event.found,
                notFound: event.notFound,
                phase: useLLM ? 'llm' : 'done',
              }));
              if (!useLLM) {
                toast.success(`Artikelsuche: ${event.found} gefunden, ${event.notFound} offen, ${event.skipped || 0} übersprungen`);
              }
            } else if (event.type === 'llm_start') {
              setV2SearchProgress(p => ({ ...p, phase: 'llm', llmTotal: event.total }));
            } else if (event.type === 'llm_progress') {
              setV2SearchProgress(p => ({
                ...p,
                llmCurrent: event.current,
                llmTotal: event.total,
                llmFound: event.llmFound,
                llmCost: event.cost,
              }));
            } else if (event.type === 'llm_done') {
              setV2SearchProgress(p => ({
                ...p,
                llmFound: event.found,
                llmCost: event.cost,
                phase: 'done',
              }));
            } else if (event.type === 'complete') {
              const llmInfo = event.fromLLM ? `, ${event.fromLLM} via LLM` : '';
              toast.success(`Artikelsuche: ${event.found} gefunden${llmInfo}, ${event.notFound} offen, ${event.skipped || 0} übersprungen`);
            } else if (event.type === 'error') {
              toast.error(event.error || 'Fehler bei der Artikelsuche');
            }
          } catch { /* ignore parse errors */ }
        }
      }

      // Reload data
      await loadData();
    } catch (err) {
      console.error('Search articles error:', err);
      toast.error(err instanceof Error && err.message ? err.message : 'Fehler bei der Artikelsuche');
    } finally {
      setV2Searching(false);
    }
  };

  // v2 Phase 4: OMD-Verifizierung (Tagespreise für alle Composite Items mit OBETA-Nr)
  const handleV2OmdVerify = async () => {
    setV2OmdRunning(true);
    setV2OmdProgress({ current: 0, total: 0, verified: 0, failed: 0 });

    try {
      const response = await fetch(`/api/gaeb/projects/${id}/verify-omd`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.substring(6));
            if (event.type === 'omd_start') {
              setV2OmdProgress(p => ({ ...p, total: event.total }));
            } else if (event.type === 'omd_progress') {
              setV2OmdProgress({
                current: event.current,
                total: event.total,
                verified: event.verified,
                failed: event.failed,
              });
            } else if (event.type === 'omd_done' || event.type === 'complete') {
              toast.success(`OMD-Verifizierung: ${event.verified}/${event.total} verifiziert${event.failed ? `, ${event.failed} fehlgeschlagen` : ''}`);
            } else if (event.type === 'omd_error' || event.type === 'error') {
              toast.error(event.error || 'OMD-Fehler');
            }
          } catch { /* ignore parse errors */ }
        }
      }

      await loadData();
    } catch (err) {
      console.error('OMD verify error:', err);
      toast.error('OMD-Verifizierung fehlgeschlagen');
    } finally {
      setV2OmdRunning(false);
    }
  };

  // v2 Auto-Resolve: Kompletter 5-Phasen-Flow
  const handleAutoResolveV2 = async () => {
    setV2AutoResolving(true);
    setV2AutoPhase(0);
    setV2AutoPhaseName('Starte...');
    setV2AutoProgress({});
    setV2GapsReport(null);

    try {
      const response = await fetch(gaeb.autoResolveV2Url(id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ skipExisting: true, useLLM: true }),
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.substring(6));

            if (event.type === 'phase_start') {
              setV2AutoPhase(event.phase);
              setV2AutoPhaseName(event.name);
            } else if (event.type === 'phase_done') {
              setV2AutoPhase(event.phase);
              setV2AutoProgress(prev => ({ ...prev, [`phase${event.phase}`]: event }));
              if (event.phase === 5 && event.missingItems) {
                setV2GapsReport(event);
              }
            } else if (event.type === 'auto_resolve_v2_complete') {
              const p = event.phases;
              toast.success(
                `Auto-Bepreisen v2 abgeschlossen in ${event.durationSec}s — ` +
                `${p.resolve?.resolved || 0} aufgelöst, ${p.search?.found || 0} gefunden, ` +
                `${p.omd?.verified || 0} OMD-verifiziert, ${p.gaps?.missingItems || 0} Lücken`
              );
            } else if (event.type === 'auto_resolve_v2_error') {
              toast.error(event.error || 'Auto-Bepreisen v2 fehlgeschlagen');
            }
            // Update sub-phase progress for display
            else if (event.type === 'p2_resolve_progress' || event.type === 'p2_resolve_batch') {
              setV2AutoPhaseName(`Auflösen: ${event.current || event.batchEnd || ''}/${event.total || '?'}`);
            } else if (event.type === 'p3_search_progress') {
              setV2AutoPhaseName(`Suche: ${event.found || 0} gefunden (${event.current || 0}/${event.total || '?'})`);
            } else if (event.type === 'p3_llm_progress') {
              setV2AutoPhaseName(`LLM: ${event.llmFound || 0} gefunden`);
            } else if (event.type === 'p4_omd_progress') {
              setV2AutoPhaseName(`OMD: ${event.verified || 0}/${event.total || '?'} verifiziert`);
            }
          } catch { /* ignore parse errors */ }
        }
      }

      await loadData();
      // Refresh profil
      try {
        const profilRes = await gaeb.getProjectProfile(id);
        setProjektProfil(profilRes.data);
      } catch { /* ignore */ }
    } catch (err) {
      console.error('Auto-Resolve v2 error:', err);
      toast.error('Auto-Bepreisen v2 fehlgeschlagen: ' + err.message);
    } finally {
      setV2AutoResolving(false);
      setV2AutoPhase(0);
      setV2AutoPhaseName('');
    }
  };

  // LLM Position Analysis (B4)
  const handleAnalyzePositions = async () => {
    setAnalyzing(true);
    setAnalyzeProgress({ current: 0, total: 0 });

    try {
      const hasExistingAnalysis = positions.some(p => p.analysis_category);
      const force = hasExistingAnalysis ? window.confirm('Analyse erneut durchführen? Vorhandene Ergebnisse werden überschrieben.') : false;
      if (hasExistingAnalysis && !force) { setAnalyzing(false); return; }
      const response = await gaeb.analyzePositions(id, { force });
      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let gotComplete = false;

      const processLine = (line) => {
        if (!line.startsWith('data: ')) return;
        try {
          const data = JSON.parse(line.slice(6));
          console.log('[Analyze SSE]', data.type, data);
          switch (data.type) {
            case 'start':
              setAnalyzeProgress({ current: 0, total: data.total ?? 0, phase: 'starting' });
              break;
            case 'progress':
              // progress fires at start of each batch
              setAnalyzeProgress((p) => ({
                ...p,
                current: data.current ?? p.current ?? 0,
                total: data.total ?? p.total ?? 0,
                phase: 'batch',
              }));
              break;
            case 'batch_done':
              setAnalyzeProgress({ current: data.current ?? 0, total: data.total ?? 0, phase: 'batch' });
              break;
            case 'complete':
              gotComplete = true;
              setAnalyzeProgress({ current: data.total ?? data.analyzed ?? 0, total: data.total ?? data.analyzed ?? 0, phase: 'complete' });
              toast.success(`Analyse abgeschlossen: ${data.analyzed} Positionen klassifiziert`);
              loadData();
              break;
            case 'error':
              toast.error(data.message);
              break;
          }
        } catch (e) {}
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) processLine(line);
      }

      // Process any remaining data in buffer after stream ends
      if (buffer.trim()) {
        for (const line of buffer.split('\n')) processLine(line);
      }

      // Fallback: always reload data after stream ends, even if 'complete' event was missed
      if (!gotComplete) loadData();
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Analyse fehlgeschlagen');
      loadData(); // Reload even on error — data may have been partially written
    } finally {
      setAnalyzing(false);
    }
  };

  // Export P94
  const handleExportP94 = async () => {
    setExportingP94(true);
    try {
      const response = await gaeb.exportP94(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.projectRef || project.name}.P94`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('P94 Export erfolgreich');
    } catch (err) {
      console.error('P94 export failed:', err);
      toast.error('P94 Export fehlgeschlagen');
    } finally {
      setExportingP94(false);
    }
  };

  // Export Rest-P93
  const handleExportP93Rest = async () => {
    setExportingP93(true);
    try {
      const response = await gaeb.exportP93Rest(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.projectRef || project.name}_Rest.P93`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Rest-P93 Export erfolgreich');
    } catch (err) {
      console.error('P93 export failed:', err);
      toast.error('Rest-P93 Export fehlgeschlagen');
    } finally {
      setExportingP93(false);
    }
  };

  // Import P94
  const handleImportP94 = async () => {
    if (!importFile) return;

    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await gaeb.importP94(id, formData);
      setImportResult(res.data);
      toast.success('P94 Import erfolgreich');
      loadData();
    } catch (err) {
      console.error('P94 import failed:', err);
      toast.error(err.response?.data?.error || 'P94 Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  // Auto-resolve positions
  const handleAutoResolve = async () => {
    setResolving(true);
    setResolveProgress({ current: 0, total: 0, position: '' });
    setResolveResult(null);

    const controller = new AbortController();
    setResolveAbort(controller);

    try {
      const response = await fetch(`/api/gaeb/projects/${id}/auto-resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skipLlm: true, skipOmd: false }),
        signal: controller.signal,
        credentials: 'include',
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let gotComplete = false;

      const processLine = (line) => {
        if (!line.startsWith('data: ')) return;
        try {
          const data = JSON.parse(line.slice(6));
          switch (data.type) {
            case 'start':
              setResolveProgress((p) => ({ ...p, total: data.total }));
              break;
            case 'progress':
              setResolveProgress({
                current: data.current,
                total: data.total,
                position: data.position || '',
              });
              break;
            case 'match':
            case 'no_match':
              setPositions((prev) =>
                prev.map((p) =>
                  p.id === data.posId
                    ? {
                        ...p,
                        match_status: data.status || (data.type === 'match' ? 'auto_matched' : 'not_found'),
                        obeta_article_number: data.article || p.obeta_article_number,
                        match_confidence: data.confidence || p.match_confidence,
                      }
                    : p
                )
              );
              break;
            case 'complete':
              gotComplete = true;
              setResolveResult({
                matched: data.matched || 0,
                notFound: data.notFound || 0,
                skipped: data.skipped || 0,
                errors: data.errors || 0,
                total: data.total || 0,
              });
              loadData();
              break;
            case 'cancelled':
              gotComplete = true;
              setResolveResult({
                matched: data.matched || 0,
                notFound: data.notFound || 0,
                skipped: data.skipped || 0,
                errors: data.errors || 0,
                total: data.total || 0,
                cancelled: true,
              });
              loadData();
              break;
            case 'error':
              toast.error(data.message || 'Auto-Resolve Fehler');
              break;
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) processLine(line);
      }

      // Process remaining buffer after stream ends
      if (buffer.trim()) {
        for (const line of buffer.split('\n')) processLine(line);
      }

      // Fallback reload if complete event was missed
      if (!gotComplete) loadData();
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Auto-resolve error:', err);
        toast.error(err instanceof Error && err.message ? err.message : 'Auto-Resolve fehlgeschlagen');
      }
    } finally {
      setResolving(false);
      setResolveAbort(null);
    }
  };

  const handleCancelResolve = () => {
    if (resolveAbort) {
      resolveAbort.abort();
    }
  };

  // OMD Live-Preise aktualisieren
  const handleOmdPricing = async () => {
    setOmdRunning(true);
    setOmdProgress({ current: 0, total: 0 });

    try {
      const response = await fetch(`/api/gaeb/projects/${id}/omd-pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      await assertStreamOk(response);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processLine = (line) => {
        if (!line.startsWith('data: ')) return;
        try {
          const data = JSON.parse(line.slice(6));
          switch (data.type) {
            case 'start':
              setOmdProgress((p) => ({ ...p, total: data.total }));
              break;
            case 'progress':
              setOmdProgress({ current: data.current, total: data.total });
              break;
            case 'complete':
              toast.success(`OMD: ${data.updated} Preise aktualisiert${data.failed ? `, ${data.failed} fehlgeschlagen` : ''}`);
              loadData();
              break;
            case 'error':
              toast.error(data.message || 'OMD-Fehler');
              break;
          }
        } catch (e) {}
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) processLine(line);
      }
      if (buffer.trim()) {
        for (const line of buffer.split('\n')) processLine(line);
      }
    } catch (err) {
      console.error('OMD pricing error:', err);
      toast.error('OMD-Preisabfrage fehlgeschlagen');
    } finally {
      setOmdRunning(false);
    }
  };

  // Open edit modal
  const handleOpenEdit = async (position) => {
    // Don't open edit modal for preamble rows
    if (isPreambleRow(position)) return;

    setEditForm({
      pr_netto: position.pr_netto ?? '',
      pr_brutto: position.pr_brutto ?? '',
      pr_basis: position.pr_basis ?? 1,
      obeta_article_number: position.obeta_article_number ?? '',
      obeta_ean: position.obeta_ean ?? '',
      obeta_kurztext: position.obeta_kurztext ?? '',
      notes: position.notes ?? '',
      match_status: position.match_status || 'pending',
      attention_resolved: position.attention_resolved || 0,
      lohnzeit_minuten: position.lohnzeit_minuten ?? null,
      manuell_geprüft: position.manuell_geprüft || 0,
    });
    setModalDatanormQuery('');
    setModalDatanormMode('fts');
    setModalDatanormResults([]);
    setCompositeItems([]);
    // compositeExpanded removed
    setOciSidebarFilter('');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setIsDirty(false);
    setSaveStatus('saved');
    setEditModal({ open: true, position });

    // Load OCI sidebar items (in background)
    loadOciSidebarItems();

    // Always load composite items (every position uses Einzelartikel now)
    if (!isPreambleRow(position)) {
      setCompositeLoading(true);
      try {
        const res = await gaeb.getCompositeItems(position.id);
        const items = res.data || [];
        setCompositeItems(items);

        // Lazy migration: old position with Hauptartikel but no Einzelartikel → auto-convert
        if (items.length === 0 && position.obeta_article_number) {
          try {
            const newRes = await gaeb.addCompositeItem(position.id, {
              quantity: 1,
              unit: position.mengeneinheit || 'Stk',
              description: position.obeta_kurztext || position.art_kurztext || 'Artikel',
            });
            const newItem = newRes.data;
            const savedRes = await gaeb.updateCompositeItem(newItem.id, {
              obeta_article_number: position.obeta_article_number,
              obeta_ean: position.obeta_ean || '',
              obeta_kurztext: position.obeta_kurztext || '',
              pr_netto: position.pr_netto || 0,
              pr_basis: position.pr_basis || 1,
              match_status: position.match_status || 'manual',
            });
            setCompositeItems([savedRes.data]);
          } catch (migErr) {
            console.error('Lazy migration failed:', migErr);
          }
        }
      } catch (err) {
        console.error('Failed to load composite items:', err);
      } finally {
        setCompositeLoading(false);
      }
    }
  };

  // Save a single composite item price
  const handleSaveCompositeItem = async (itemId, data) => {
    setSavingCompositeId(itemId);
    try {
      const res = await gaeb.updateCompositeItem(itemId, data);
      const updatedItems = compositeItems.map((item) => (item.id === itemId ? res.data : item));
      setCompositeItems(updatedItems);
      recalcEP(updatedItems);
      toast.success('Artikel gespeichert');
    } catch (err) {
      console.error('Save composite item failed:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingCompositeId(null);
    }
  };

  // Extract composite articles via LLM
  const handleExtractComposite = async (forceLlm = true) => {
    if (!editModal.position) return;
    setExtracting(true);
    try {
      const res = await gaeb.extractComposite(id, editModal.position.id, { forceLlm });
      const articles = res.data.articles || [];
      setCompositeItems(articles);
      // compositeExpanded removed — Einzelartikel section is always visible
      recalcEP(articles);
      toast.success(`${res.data.count || 0} Einzelartikel extrahiert (${res.data.source})`);
    } catch (err) {
      console.error('Extract composite failed:', err);
      toast.error('Extraktion fehlgeschlagen: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setExtracting(false);
    }
  };

  // Add a composite item
  const handleAddCompositeItem = async () => {
    if (!editModal.position) return;
    try {
      const res = await gaeb.addCompositeItem(editModal.position.id, {
        quantity: 1,
        unit: 'Stk',
        description: 'Neuer Artikel',
      });
      setCompositeItems(prev => [...prev, res.data]);
      // compositeExpanded removed
    } catch (err) {
      console.error('Add composite item failed:', err);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  // Delete a composite item
  const handleDeleteCompositeItem = async (itemId) => {
    try {
      await gaeb.deleteCompositeItem(itemId);
      const remaining = compositeItems.filter(ci => ci.id !== itemId);
      setCompositeItems(remaining);
      recalcEP(remaining);
      toast.success('Artikel entfernt');
    } catch (err) {
      console.error('Delete composite item failed:', err);
      toast.error('Fehler beim Löschen');
    }
  };

  // Verify position — save all articles to product catalog + mappings
  const handleVerifyPosition = async () => {
    if (!editModal.position) return;
    try {
      const res = await gaeb.verifyPosition(id, editModal.position.id);
      const d = res.data;
      toast.success(`${d.totalArticles} Artikel in Katalog übernommen (${d.productsCreated} neu, ${d.productsUpdated} aktualisiert)`);
      setEditForm(prev => ({ ...prev, match_status: 'verified' }));
      setPositions(prev => prev.map(p =>
        p.id === editModal.position.id ? { ...p, match_status: 'verified' } : p
      ));
      // Update composite items status locally
      if (compositeItems.length > 0) {
        setCompositeItems(prev => prev.map(ci =>
          ci.obeta_article_number && ci.pr_netto > 0 ? { ...ci, match_status: 'verified' } : ci
        ));
      }
    } catch (err) {
      console.error('Verify position failed:', err);
      toast.error('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  // Verify single composite item — save to catalog
  const handleVerifySingleItem = async (itemId) => {
    if (!editModal.position) return;
    try {
      const res = await gaeb.verifySingleItem(id, editModal.position.id, itemId);
      const d = res.data;
      toast.success(`Artikel in Katalog übernommen (${d.productsCreated} neu, ${d.productsUpdated} aktualisiert)`);
      setCompositeItems(prev => prev.map(ci =>
        ci.id === itemId ? { ...ci, match_status: 'verified' } : ci
      ));
    } catch (err) {
      console.error('Verify single item failed:', err);
      toast.error('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const hasArticlesForVerify =
    compositeItems.length > 0 && compositeItems.some(ci => ci.obeta_article_number && ci.pr_netto > 0);

  // Load composite templates (filtered by category of current position)
  const loadTemplates = async (category) => {
    try {
      const res = await gaeb.getCompositeTemplates(category || null);
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Load templates error:', err);
      setTemplates([]);
    }
  };

  // Save current composite items as template
  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !editModal.position) return;
    setSavingTemplate(true);
    try {
      await gaeb.createCompositeTemplate({
        name: templateName.trim(),
        category: templateCategory || editModal.position.analysis_category || null,
        sourcePositionId: editModal.position.id,
      });
      toast.success('Vorlage gespeichert');
      setShowTemplateSave(false);
      setTemplateName('');
    } catch (err) {
      console.error('Save template error:', err);
      toast.error('Vorlage konnte nicht gespeichert werden');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Apply a template to the current position
  const handleApplyTemplate = async (templateId) => {
    if (!editModal.position) return;
    if (compositeItems.length > 0 && !window.confirm('Vorhandene Artikel werden beibehalten. Vorlage-Artikel werden hinzugefügt. Fortfahren?')) return;
    setApplyingTemplate(true);
    try {
      const res = await gaeb.applyCompositeTemplate(id, editModal.position.id, templateId);
      const templateItems = res.data.items || [];
      setCompositeItems(templateItems);
      // compositeExpanded removed
      setShowTemplateList(false);
      recalcEP(templateItems);
      toast.success(`${res.data.count || 0} Artikel aus Vorlage übernommen`);
    } catch (err) {
      console.error('Apply template error:', err);
      toast.error('Vorlage konnte nicht angewendet werden');
    } finally {
      setApplyingTemplate(false);
    }
  };

  // OCI sidebar: load all OCI import items
  const loadOciSidebarItems = async () => {
    setOciSidebarLoading(true);
    try {
      const res = await api.get('/oci/results');
      const callbacks = res.data || [];
      const items = callbacks
        .filter(cb => cb.callbackType !== 'bulk_update')
        .flatMap(cb => (cb.items || []).map(item => ({ ...item, callbackId: cb.id, supplier: cb.supplier })));
      setOciSidebarItems(items);
    } catch (e) {
      console.warn('[OCI Sidebar] Failed to load:', e.message);
    } finally {
      setOciSidebarLoading(false);
    }
  };

  // Catalog sidebar: load products
  const loadCatalogProducts = async (search) => {
    setCatalogLoading(true);
    try {
      if (search && search.length >= 2) {
        const res = await productsApi.search(search);
        setCatalogProducts(Array.isArray(res.data) ? res.data : []);
      } else {
        const res = await productsApi.getAll();
        const all = Array.isArray(res.data) ? res.data : [];
        setCatalogProducts(all.slice(0, 50));
      }
    } catch (e) {
      console.warn('[Catalog Sidebar] Failed to load:', e.message);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Load catalog when tab switches
  useEffect(() => {
    if (sidebarTab === 'catalog' && catalogProducts.length === 0) {
      loadCatalogProducts('');
    }
  }, [sidebarTab]);

  // Debounced catalog search
  useEffect(() => {
    if (sidebarTab !== 'catalog') return;
    const timer = setTimeout(() => {
      loadCatalogProducts(catalogFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [catalogFilter]);

  const catalogItemToOci = (product) => ({
    vendorMat: product.article_number || product.articleNumber || '',
    gtin: product.gtin || '',
    description: product.name || '',
    price: product.price || 0,
  });

  // Recalculate EP Netto from composite items sum
  const recalcEP = (items) => {
    const list = items || compositeItems;
    const total = list.reduce((sum, ci) => {
      return sum + (ci.pr_netto * ci.quantity / (ci.pr_basis > 1 ? ci.pr_basis : 1));
    }, 0);
    if (total > 0) {
      setEditForm(prev => ({
        ...prev,
        pr_netto: total.toFixed(2),
        match_status: prev.match_status === 'pending' ? 'manual' : prev.match_status,
      }));
      triggerAutoSave();
    }
  };

  // Clipboard: copy single article
  const handleCopyCompositeItem = (item) => {
    const clipItem = {
      description: item.description,
      obeta_article_number: item.obeta_article_number,
      obeta_ean: item.obeta_ean,
      obeta_kurztext: item.obeta_kurztext,
      pr_netto: item.pr_netto,
      pr_basis: item.pr_basis,
      unit: item.unit,
      quantity: item.quantity,
      manufacturer: item.manufacturer,
      match_status: item.match_status,
    };
    setClipboardItems(prev => {
      if (clipItem.obeta_article_number && prev.some(c => c.obeta_article_number === clipItem.obeta_article_number)) return prev;
      return [...prev, clipItem];
    });
    toast.success('Artikel kopiert');
  };

  // Clipboard: copy all articles from current position
  const handleCopyAllCompositeItems = () => {
    const items = compositeItems
      .filter(ci => ci.obeta_article_number || ci.description)
      .map(ci => ({
        description: ci.description,
        obeta_article_number: ci.obeta_article_number,
        obeta_ean: ci.obeta_ean,
        obeta_kurztext: ci.obeta_kurztext,
        pr_netto: ci.pr_netto,
        pr_basis: ci.pr_basis,
        unit: ci.unit,
        quantity: ci.quantity,
        manufacturer: ci.manufacturer,
        match_status: ci.match_status,
      }));
    setClipboardItems(items);
    toast.success(`${items.length} Artikel kopiert`);
  };

  // Clipboard: paste all into current position
  const handlePasteClipboard = async () => {
    if (clipboardItems.length === 0) return;
    for (const clipItem of clipboardItems) {
      await addAsCompositeItem({
        articleNumber: clipItem.obeta_article_number,
        ean: clipItem.obeta_ean || '',
        description: clipItem.obeta_kurztext || clipItem.description,
        price: clipItem.pr_netto || 0,
        priceBase: clipItem.pr_basis || 1,
        unit: clipItem.unit || 'Stk',
        matchStatus: clipItem.match_status || 'manual',
      });
    }
    toast.success(`${clipboardItems.length} Artikel eingefügt`);
  };

  // Unified: add any article as new composite item (always Einzelartikel)
  const addAsCompositeItem = async ({ articleNumber, ean, description, price, priceBase, unit, matchStatus }) => {
    if (!editModal.position) return;
    try {
      const res = await gaeb.addCompositeItem(editModal.position.id, {
        quantity: 1,
        unit: unit || 'Stk',
        description: description || 'Neuer Artikel',
      });
      const newItem = res.data;
      const savedRes = await gaeb.updateCompositeItem(newItem.id, {
        obeta_article_number: articleNumber || '',
        obeta_ean: ean || '',
        obeta_kurztext: description || '',
        pr_netto: price || 0,
        pr_basis: priceBase || 1,
        match_status: matchStatus || (price > 0 ? 'manual' : 'pending'),
      });
      setCompositeItems(prev => {
        const updated = [...prev, savedRes.data];
        recalcEP(updated);
        return updated;
      });
    } catch (err) {
      console.error('Add composite item failed:', err);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  // Sidebar [→]: always add as new Einzelartikel
  const handleApplyOciToField = (item) => {
    addAsCompositeItem({
      articleNumber: item.vendorMat || item.article_number || '',
      ean: item.gtin || '',
      description: item.description || item.name || '',
      price: item.price || 0,
      unit: item.unitOci === 'MTR' ? 'm' : 'Stk',
      matchStatus: 'oci_matched',
    });
  };

  // OCI sidebar: delete a callback
  const handleDeleteOciCallback = async (callbackId) => {
    try {
      await api.delete(`/oci/results/${callbackId}`);
      setOciSidebarItems(prev => prev.filter(i => i.callbackId !== callbackId));
    } catch (e) {
      toast.error('Fehler beim Löschen: ' + e.message);
    }
  };

  // Save position edit (closeModal=false keeps the modal open)
  const handleSavePosition = async (closeModal = false) => {
    if (!editModal.position) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    setSaving(true);
    setSaveStatus('saving');
    try {
      const data = {
        ...editForm,
        pr_netto: editForm.pr_netto !== '' ? parseFloat(editForm.pr_netto) : null,
        pr_brutto: editForm.pr_brutto !== '' ? parseFloat(editForm.pr_brutto) : null,
        pr_basis: editForm.pr_basis !== '' ? parseFloat(editForm.pr_basis) : 1,
        attention_resolved: editForm.attention_resolved ? 1 : 0,
        lohnzeit_minuten: editForm.lohnzeit_minuten !== null && editForm.lohnzeit_minuten !== '' ? parseFloat(editForm.lohnzeit_minuten) : null,
        manuell_geprüft: editForm.manuell_geprüft ? 1 : 0,
      };
      await gaeb.updatePosition(id, editModal.position.id, data);
      setSaveStatus('saved');
      setIsDirty(false);

      // Update position in local state (no full reload needed)
      setPositions(prev => prev.map(p =>
        p.id === editModal.position.id
          ? { ...p, ...data, pr_netto: parseFloat(data.pr_netto) || 0, pr_brutto: parseFloat(data.pr_brutto) || 0, pr_basis: parseFloat(data.pr_basis) || 1 }
          : p
      ));

      if (closeModal) {
        setEditModal({ open: false, position: null });
      }
    } catch (err) {
      console.error('Save position failed:', err);
      setSaveStatus('error');
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Keep ref to latest handleSavePosition for auto-save timer
  handleSaveRef.current = handleSavePosition;

  // Debounced auto-save: triggers 1.5s after last change
  const triggerAutoSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setIsDirty(true);
    setSaveStatus('dirty');
    saveTimerRef.current = setTimeout(() => {
      handleSaveRef.current?.(false);
    }, 1500);
  }, []);

  // Single field change handler with auto-save
  const handleFormChange = useCallback((field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    triggerAutoSave();
  }, [triggerAutoSave]);

  // Navigation: save first, then switch position
  const handleNavigate = async (targetPos) => {
    if (isDirty) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      await handleSavePosition(false);
    }
    handleOpenEdit(targetPos);
  };

  // Apply datanorm result — always as new Einzelartikel
  const handleApplyDatanorm = (item) => {
    const rawPrice = item.netPrice ?? item.net_price ?? item.price ?? 0;
    const priceBase = item.priceBase ?? item.price_base ?? 1;
    const perUnitPrice = priceBase > 1 ? (rawPrice / priceBase) : rawPrice;
    const shortText1 = item.shortText1 ?? item.short_text_1 ?? '';
    const shortText2 = item.shortText2 ?? item.short_text_2 ?? '';
    const kurztext = [shortText1, shortText2].filter(Boolean).join(' ');
    const artNr = item.articleNumber ?? item.article_number ?? '';

    // Prüfe ob Artikel schon existiert → updaten statt neu anlegen
    const existing = compositeItems.find(ci => ci.obeta_article_number === artNr);
    if (existing) {
      handleSaveCompositeItem(existing.id, {
        obeta_article_number: artNr,
        obeta_ean: item.ean ?? item.gtin ?? '',
        obeta_kurztext: kurztext,
        description: kurztext,
        pr_netto: perUnitPrice,
        pr_basis: 1,
        match_status: 'manual',
      });
    } else {
      addAsCompositeItem({
        articleNumber: artNr,
        ean: item.ean ?? item.gtin ?? '',
        description: kurztext || 'Datanorm Artikel',
        price: perUnitPrice,
        priceBase: 1,
        unit: item.unit || 'Stk',
        matchStatus: 'manual',
      });
    }
    setModalDatanormQuery('');
    setModalDatanormResults([]);
  };

  // Auto-fill search from langtext (C1)
  const handleAutoFillSearch = () => {
    if (!editModal.position) return;
    const text = editModal.position.art_langtext_full || editModal.position.art_langtext || '';
    const terms = extractSearchTerms(text);
    if (terms) {
      setModalDatanormQuery(terms);
      setModalDatanormMode('fts');
    }
  };

  // Get ALL hierarchical context preambles for current edit position (OZ prefix matching)
  const contextPreambles = useMemo(() => {
    if (!editModal.position) return [];
    return findContextPreambles(positions, editModal.position);
  }, [editModal.position, positions]);

  // Attention hints for current edit position
  const attentionHints = useMemo(() => {
    if (!editModal.position) return [];
    const text = editModal.position.art_langtext_full || editModal.position.art_langtext || '';
    return detectAttentionHints(text);
  }, [editModal.position]);

  // Navigation: compute navigable positions (skip preambles)
  const navigablePositions = useMemo(() => filteredPositions.filter(p => !isPreambleRow(p)), [filteredPositions]);
  const navIndex = useMemo(() => {
    if (!editModal.position) return -1;
    return navigablePositions.findIndex(p => p.id === editModal.position.id);
  }, [editModal.position, navigablePositions]);
  const prevPos = navIndex > 0 ? navigablePositions[navIndex - 1] : null;
  const nextPos = navIndex < navigablePositions.length - 1 ? navigablePositions[navIndex + 1] : null;

  // Keyboard navigation: ArrowLeft/ArrowRight when modal is open
  // Prevent body scroll when edit modal open
  useEffect(() => {
    if (editModal.open) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [editModal.open]);

  useEffect(() => {
    if (!editModal.open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSaveRef.current?.(true); // save + close
        return;
      }
      // Don't navigate when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'ArrowLeft' && prevPos) {
        e.preventDefault();
        handleNavigate(prevPos);
      } else if (e.key === 'ArrowRight' && nextPos) {
        e.preventDefault();
        handleNavigate(nextPos);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editModal.open, prevPos, nextPos, isDirty]);

  // Cleanup save timer on unmount
  useEffect(() => {
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, []);

  // Stats
  const stats = project?.stats || {};

  if (loading) {
    return <Spinner.Center />;
  }

  if (!project) {
    return null;
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title={project.name || 'Kalkulation'}
        description={project.projectRef ? `Projektreferenz: ${project.projectRef}` : undefined}
        showDate={false}
        actions={
          <div className="flex items-center gap-2">
            {/* HAUPTBUTTON: Kalkulation starten (alle Phasen automatisch) */}
            {(() => {
              const isRunning = v2AutoResolving || v2Resolving || v2Searching || v2OmdRunning || analyzing || resolving || omdRunning;
              return (
                <Button
                  variant={isRunning ? 'outline' : 'default'}
                  icon={isRunning ? RefreshCw : Rocket}
                  onClick={handleAutoResolveV2}
                  disabled={isRunning}
                  className={`${isRunning ? '[&>svg]:animate-spin' : ''} ${!isRunning ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 border-0 px-6' : ''}`}
                  title="Kompletter automatischer Ablauf: Profil → Stücklisten → Artikelsuche → Preise → Lücken-Report"
                >
                  {v2AutoResolving
                    ? `Phase ${v2AutoPhase}/5: ${v2AutoPhaseName}`
                    : v2Resolving
                    ? `${v2ResolveProgress.current}/${v2ResolveProgress.total} auflösen...`
                    : v2Searching
                    ? v2SearchProgress.phase === 'llm'
                      ? `LLM: ${v2SearchProgress.llmFound || 0} gefunden...`
                      : `${v2SearchProgress.found}/${v2SearchProgress.current || v2SearchProgress.total} Artikel gefunden...`
                    : v2OmdRunning
                    ? `OMD: ${v2OmdProgress.verified}/${v2OmdProgress.total} Preise...`
                    : 'Kalkulation starten'}
                </Button>
              );
            })()}

            {/* ERWEITERT-DROPDOWN: Einzelschritte für Experten */}
            <div className="relative" data-expert-menu>
              <Button
                variant="outline"
                icon={Settings}
                onClick={() => setShowExpertMenu(prev => !prev)}
                className="px-2"
                title="Einzelschritte & Experten-Funktionen"
              />
              {showExpertMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 w-56">
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-400 uppercase">Einzelschritte</div>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    onClick={() => { setShowExpertMenu(false); handleResolvePositions(); }}
                    disabled={v2Resolving || v2AutoResolving || v2Searching}
                  >
                    <Zap className="w-4 h-4 text-orange-500" /> Stücklisten erstellen
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    onClick={() => { setShowExpertMenu(false); handleSearchArticles(false); }}
                    disabled={v2Searching || v2Resolving || v2AutoResolving}
                  >
                    <Search className="w-4 h-4 text-blue-500" /> Artikel suchen (Datanorm)
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    onClick={() => { setShowExpertMenu(false); handleSearchArticles(true); }}
                    disabled={v2Searching || v2Resolving || v2AutoResolving}
                  >
                    <Sparkles className="w-4 h-4 text-purple-500" /> + LLM-Experte
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    onClick={() => { setShowExpertMenu(false); handleV2OmdVerify(); }}
                    disabled={v2OmdRunning || v2Searching || v2Resolving || v2AutoResolving}
                  >
                    <CheckCircle className="w-4 h-4 text-green-500" /> OMD-Preise aktualisieren
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-400 uppercase">Export / Import</div>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => { setShowExpertMenu(false); handleExportP94(); }}
                  >
                    <Download className="w-4 h-4 text-gray-500" /> P94 Export
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => { setShowExpertMenu(false); handleExportP93Rest(); }}
                  >
                    <FileText className="w-4 h-4 text-gray-500" /> Rest-P93 Export
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    onClick={() => { setShowExpertMenu(false); setImportFile(null); setImportResult(null); setImportModal(true); }}
                  >
                    <Upload className="w-4 h-4 text-gray-500" /> P94 Import
                  </button>
                </div>
              )}
            </div>

            {/* ZURÜCK */}
            <Link to="/admin/kalkulation">
              <Button variant="outline" icon={ArrowLeft}>
                Zurück
              </Button>
            </Link>
          </div>
        }
      />

      {/* Kalkulations-Einstellungen (BUG-007 + BUG-008) */}
      <KalkulationsSettingsPanel projectId={id} onSettingsChange={setKalkSettings} />

      {/* Projektprofil v2 Panel */}
      <div className="mb-4">
        {projektProfil ? (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Header */}
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 transition"
              onClick={() => setProfilExpanded(!profilExpanded)}
            >
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-indigo-600" />
                <span className="font-semibold text-gray-800">Projektprofil v2</span>
                {projektProfil.projekt?.wohneinheiten && (
                  <span className="text-sm text-gray-500">({projektProfil.projekt.wohneinheiten} WE)</span>
                )}
                <span className="text-xs text-gray-400 ml-2">
                  ${projektProfil.meta?.llm_cost?.toFixed(4) || '?'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {projektProfil.warnhinweise?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> {projektProfil.warnhinweise.length}
                  </span>
                )}
                <Button
                  size="xs"
                  variant="outline"
                  icon={profilLoading ? RefreshCw : RefreshCw}
                  onClick={(e) => { e.stopPropagation(); handleCreateProfile(true); }}
                  disabled={profilLoading}
                  className={profilLoading ? '[&>svg]:animate-spin' : ''}
                >
                  Neu
                </Button>
                {profilExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </div>
            </button>

            {/* Content */}
            {profilExpanded && (
              <div className="px-4 py-3 space-y-3">
                {/* Fabrikate Pills */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Fabrikate</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const fab = projektProfil.fabrikate || {};
                      const pills = [];
                      const fabrikatConfig = [
                        { key: 'up_programm', label: 'UP', color: 'bg-blue-100 text-blue-700 border-blue-200' },
                        { key: 'ap_programm', label: 'AP', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                        { key: 'cee', label: 'CEE', color: 'bg-red-100 text-red-700 border-red-200' },
                        { key: 'trassen', label: 'Trassen', color: 'bg-orange-100 text-orange-700 border-orange-200' },
                        { key: 'verteilungen', label: 'Verteiler', color: 'bg-green-100 text-green-700 border-green-200' },
                        { key: 'sprechanlage', label: 'Sprech', color: 'bg-purple-100 text-purple-700 border-purple-200' },
                        { key: 'potentialausgleich', label: 'PA', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                        { key: 'datenverkabelung', label: 'Daten', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
                      ];
                      for (const { key, label, color } of fabrikatConfig) {
                        const f = fab[key];
                        if (!f) continue;
                        const h = f.hersteller || 'nicht_angegeben';
                        if (h === 'nicht_angegeben') continue;
                        const serie = f.serie && f.serie !== 'nicht_angegeben' ? ` ${f.serie}` : '';
                        const farbe = f.farbe && f.farbe !== 'nicht_angegeben' ? ` (${f.farbe})` : '';
                        const isDefault = f.hinweis?.includes('Compeer-Standard');
                        pills.push(
                          <span
                            key={key}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${color} ${isDefault ? 'opacity-60' : ''}`}
                            title={isDefault ? 'Compeer-Standard (nicht im LV)' : `Aus LV-Vortext`}
                          >
                            <span className="font-medium">{label}:</span>
                            {h}{serie}{farbe}
                            {isDefault && <Settings className="h-3 w-3 ml-0.5" />}
                          </span>
                        );
                      }
                      return pills.length > 0 ? pills : <span className="text-xs text-gray-400">Keine Fabrikate erkannt</span>;
                    })()}
                  </div>
                </div>

                {/* Quick Stats Row */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  {projektProfil.projekt?.installationsart && (
                    <span>Installation: <b className="text-gray-700">{projektProfil.projekt.installationsart}</b></span>
                  )}
                  {projektProfil.projekt?.auftraggeber && projektProfil.projekt.auftraggeber !== 'nicht_angegeben' && (
                    <span>AG: <b className="text-gray-700">{projektProfil.projekt.auftraggeber}</b></span>
                  )}
                  <span>Einzukalkulieren: <b className="text-gray-700">{projektProfil.einzukalkulieren?.length || 0}</b></span>
                  <span>Regeln: <b className="text-gray-700">{projektProfil.technische_regeln?.length || 0}</b></span>
                  {projektProfil.externe_quellen?.length > 0 && (
                    <span>Externe Quellen: <b className="text-gray-700">{projektProfil.externe_quellen.length}</b></span>
                  )}
                </div>

                {/* Warnhinweise (collapsible) */}
                {projektProfil.warnhinweise?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <h5 className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Warnhinweise ({projektProfil.warnhinweise.length})
                    </h5>
                    <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
                      {projektProfil.warnhinweise.map((w, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-amber-400 mt-0.5">&#8226;</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* No profile yet — show create button */
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500">
              <Brain className="h-5 w-5" />
              <span className="text-sm">Kein Projektprofil vorhanden</span>
              <span className="text-xs text-gray-400">(analysiert Vortexte mit KI)</span>
            </div>
            <Button
              size="sm"
              variant="primary"
              icon={profilLoading ? RefreshCw : Brain}
              onClick={() => handleCreateProfile(false)}
              disabled={profilLoading}
              className={profilLoading ? '[&>svg]:animate-spin' : ''}
            >
              {profilLoading ? 'Erstellt...' : 'Projektprofil erstellen'}
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-sm text-gray-500">Gesamt</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500">Artikel zugewiesen</p>
          <p className="text-2xl font-bold text-blue-600">
            {(stats.matched || 0) + (stats.manual || 0)}
          </p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500">Offen</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500">Nicht gefunden</p>
          <p className="text-2xl font-bold text-red-600">{stats.notFound || 0}</p>
        </Card>
        <Card className="text-center">
          <p className="text-sm text-gray-500">Nettosumme</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(stats.totalNetPrice || 0)}
          </p>
        </Card>
      </div>

      {/* v2 Lücken-Report Panel (nach Auto-Bepreisen) */}
      {v2GapsReport && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Lücken-Report ({v2GapsReport.totalMissingItems} fehlende Artikel)
            </h3>
            <button
              onClick={() => setV2GapsReport(null)}
              className="text-amber-500 hover:text-amber-700 text-xs"
            >
              Schließen
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
            <div className="bg-white rounded px-3 py-2">
              <span className="text-gray-500">Vollständig:</span>
              <span className="ml-1 font-bold text-green-600">{v2GapsReport.positionsFullyMatched}</span>
              <span className="text-gray-400">/{v2GapsReport.totalPositions}</span>
            </div>
            <div className="bg-white rounded px-3 py-2">
              <span className="text-gray-500">Mit Lücken:</span>
              <span className="ml-1 font-bold text-amber-600">{v2GapsReport.positionsWithGaps}</span>
            </div>
            <div className="bg-white rounded px-3 py-2">
              <span className="text-gray-500">OMD-verifiziert:</span>
              <span className="ml-1 font-bold text-emerald-600">{v2GapsReport.positionsOmdVerified}</span>
            </div>
            <div className="bg-white rounded px-3 py-2">
              <span className="text-gray-500">Warnungen:</span>
              <span className="ml-1 font-bold text-red-600">{v2GapsReport.mitWarnungen}</span>
            </div>
          </div>
          {v2GapsReport.missingItems && v2GapsReport.missingItems.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-amber-700 font-medium mb-2">
                Fehlende Artikel anzeigen ({Math.min(v2GapsReport.missingItems.length, 50)} von {v2GapsReport.totalMissingItems})
              </summary>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {v2GapsReport.missingItems.map((item, idx) => (
                  <div key={idx} className="bg-white rounded px-2 py-1 flex items-center gap-2">
                    <span className="text-gray-400 font-mono">{item.oz}</span>
                    <span className="text-gray-700 truncate flex-1">{item.description}</span>
                    {item.suchbegriff && <span className="text-blue-500 text-[10px]">🔍 {item.suchbegriff}</span>}
                    {item.artikelnummer_hint && <span className="text-green-500 text-[10px]">📌 {item.artikelnummer_hint}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Category stats bar — only show when analysis has been run */}
      {positions.some(p => p.analysis_category) && (
        <div className="mb-4 px-4 py-2 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium text-gray-500">Analyse:</span>
            {Object.entries(
              positions.reduce((acc, p) => {
                if (p.analysis_category) acc[p.analysis_category] = (acc[p.analysis_category] || 0) + 1;
                return acc;
              }, {})
            ).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
              <button
                key={cat}
                className={`px-2 py-0.5 rounded-full ${CATEGORY_BADGES[cat]?.color || 'bg-gray-100'} ${categoryFilter === cat ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
                onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                title={`Filter: ${CATEGORY_BADGES[cat]?.label || cat}`}
              >
                {CATEGORY_BADGES[cat]?.label || cat}: {count}
              </button>
            ))}
            {categoryFilter && (
              <button
                className="text-gray-400 hover:text-gray-600 underline"
                onClick={() => setCategoryFilter('')}
              >
                Filter aufheben
              </button>
            )}
          </div>
        </div>
      )}

      {/* Analyze Progress */}
      {analyzing && (
        <Card className="mb-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary-600 animate-pulse" />
                <span className="text-sm font-medium text-gray-700">
                  Positionen werden analysiert...
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {analyzeProgress.current || 0} / {analyzeProgress.total || 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                style={{
                  width: analyzeProgress.total > 0
                    ? `${Math.min(100, (analyzeProgress.current / analyzeProgress.total) * 100)}%`
                    : '0%',
                }}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Auto-Resolve Progress */}
      {(resolving || resolveResult) && (
        <Card className="mb-6">
          {resolving && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary-600 animate-pulse" />
                  <span className="text-sm font-medium text-gray-700">
                    Automatische Bepreisung läuft...
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {resolveProgress.current} / {resolveProgress.total}
                  {resolveProgress.position && (
                    <span className="ml-2 text-gray-400">({resolveProgress.position})</span>
                  )}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: resolveProgress.total > 0
                      ? `${(resolveProgress.current / resolveProgress.total) * 100}%`
                      : '0%',
                  }}
                />
              </div>
            </div>
          )}
          {resolveResult && !resolving && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {resolveResult.cancelled ? 'Abgebrochen' : 'Bepreisung abgeschlossen'}
                </span>
                <button
                  className="text-xs text-gray-400 hover:text-gray-600"
                  onClick={() => setResolveResult(null)}
                >
                  Ausblenden
                </button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-2 bg-green-50 rounded-lg">
                  <p className="text-lg font-bold text-green-700">{resolveResult.matched}</p>
                  <p className="text-xs text-green-600">Gefunden</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded-lg">
                  <p className="text-lg font-bold text-red-700">{resolveResult.notFound}</p>
                  <p className="text-xs text-red-600">Nicht gefunden</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-700">{resolveResult.skipped}</p>
                  <p className="text-xs text-gray-600">Übersprungen</p>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded-lg">
                  <p className="text-lg font-bold text-yellow-700">{resolveResult.errors}</p>
                  <p className="text-xs text-yellow-600">Fehler</p>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Search/Filter Bar */}
      <Card className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Text search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Suche nach OZ, Beschreibung, Artikelnr..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="w-full md:w-48">
            <select
              className="select appearance-none"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Category filter (v1 + v2) */}
          {positions.some(p => p.analysis_category || p.position_kategorie) && (
            <div className="w-full md:w-48">
              <select
                className="select appearance-none text-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Alle Kategorien</option>
                <optgroup label="v2 Kategorien">
                  {Object.entries(V2_KATEGORIE_BADGES).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label} ({cfg.fullLabel})</option>
                  ))}
                  <option value="_geprüft">✅ Geprüft</option>
                  <option value="_warnung">⚠️ Mit Warnungen</option>
                  <option value="_offen">🔴 Offen</option>
                </optgroup>
                <optgroup label="v1 Analyse">
                  {Object.entries(CATEGORY_BADGES).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}

          {/* A1: Vortexte toggle */}
          <button
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors shrink-0 ${
              showPreambles
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
            }`}
            onClick={() => setShowPreambles(!showPreambles)}
            title={showPreambles ? 'Vortexte ausblenden' : 'Vortexte anzeigen'}
          >
            {showPreambles ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            Vortexte
          </button>

          {/* Datanorm search */}
          <div className="flex-1 relative" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <Calculator className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Datanorm-Suche..."
                value={datanormQuery}
                onChange={(e) => setDatanormQuery(e.target.value)}
                onFocus={() => {
                  if (datanormResults.length > 0) setShowDatanormDropdown(true);
                }}
              />
              {datanormSearching && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>
            {showDatanormDropdown && datanormResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {datanormResults.map((item, idx) => (
                  <div
                    key={idx}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.article_number || item.articleNumber}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {item.description || item.name}
                        </p>
                      </div>
                      <div className="text-right ml-4 shrink-0">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(item.price || item.net_price)}
                        </p>
                        <p className="text-xs text-gray-500">{item.unit || 'Stk'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* FEATURE-002: Gesamt-Kalkulationsübersicht */}
      {positions.length > 0 && kalkSettings && (
        <div className="mb-4 bg-white border border-gray-200 rounded-lg px-4 py-3">
          {(() => {
            const aufschlagProzent = kalkSettings?.aufschlag_material_prozent ?? 40;
            const stundensatz = kalkSettings?.stundensatz_monteur ?? 52;
            const bgkGesamt = kalkSettings?.bgk_gesamt ?? 0;

            let totalMaterialEK = 0;
            let totalLohnMinuten = 0;
            let totalGP = 0;
            let positionsBepreist = 0;
            let positionsGesamt = positions.filter(p => p.pos_kenn_z === 'N' && p.menge > 0).length;

            for (const pos of positions) {
              if (pos.pos_kenn_z !== 'N' || pos.menge <= 0) continue;
              const ep = parseFloat(pos.pr_netto) || 0;
              if (ep > 0) positionsBepreist++;
              const menge = parseFloat(pos.menge) || 0;
              const basis = pos.pr_basis > 1 ? pos.pr_basis : 1;
              totalMaterialEK += ep * menge / basis;
              totalLohnMinuten += (parseFloat(pos.lohnzeit_minuten) || 0) * menge;
            }

            const totalMaterialAufschlag = totalMaterialEK * (aufschlagProzent / 100);
            const totalLohnKosten = (totalLohnMinuten / 60) * stundensatz;
            const angebotssumme = totalMaterialEK + totalMaterialAufschlag + totalLohnKosten + bgkGesamt;
            const gewinnMarge = totalMaterialAufschlag + bgkGesamt; // Vereinfacht

            return (
              <div className="flex items-center justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-1">
                  <Calculator className="h-5 w-5 text-gray-400" />
                  <span className="font-semibold text-gray-800">Kalkulation</span>
                  <span className="text-xs text-gray-400 ml-1">({positionsBepreist}/{positionsGesamt} bepreist)</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400">Material EK</div>
                    <div className="font-medium text-gray-700">{formatCurrency(totalMaterialEK)}</div>
                  </div>
                  <div className="text-gray-300">+</div>
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400">+{aufschlagProzent}%</div>
                    <div className="font-medium text-blue-600">{formatCurrency(totalMaterialAufschlag)}</div>
                  </div>
                  <div className="text-gray-300">+</div>
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400">Lohn ({Math.round(totalLohnMinuten)}min)</div>
                    <div className="font-medium text-orange-600">{formatCurrency(totalLohnKosten)}</div>
                  </div>
                  {bgkGesamt > 0 && (
                    <>
                      <div className="text-gray-300">+</div>
                      <div className="text-center">
                        <div className="text-[10px] text-gray-400">BGK</div>
                        <div className="font-medium text-amber-600">{formatCurrency(bgkGesamt)}</div>
                      </div>
                    </>
                  )}
                  <div className="text-gray-300">=</div>
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400">Angebotssumme</div>
                    <div className="text-lg font-bold text-gray-900">{formatCurrency(angebotssumme)}</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Positions Table */}
      <Card padding={false}>
        <Table.Loading loading={loading}>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header className="w-16">Nr</Table.Header>
                <Table.Header className="w-24">OZ</Table.Header>
                <Table.Header>Beschreibung</Table.Header>
                <Table.Header className="w-32 text-right">Menge</Table.Header>
                <Table.Header className="w-16 text-right" title="Lohnzeit in Minuten pro Einheit">Lohn</Table.Header>
                <Table.Header className="w-32 text-right">EP Netto</Table.Header>
                <Table.Header className="w-32 text-right">GP Netto</Table.Header>
                <Table.Header className="w-32 text-right" title="GP inkl. Materialaufschlag + Lohnkosten">GP inkl. Lohn</Table.Header>
                <Table.Header className="w-36">Artikel</Table.Header>
                <Table.Header className="w-36 text-center">Status</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {filteredPositions.length === 0 ? (
                <Table.Empty
                  message={
                    positions.length === 0
                      ? 'Keine Positionen vorhanden'
                      : 'Keine Positionen gefunden'
                  }
                  colSpan={9}
                />
              ) : (
                filteredPositions.map((pos) => {
                  const preamble = isPreambleRow(pos);
                  const gpNetto = preamble ? null : calculateGP(pos.menge, pos.pr_netto, pos.pr_basis);
                  // GP inkl. Lohn: Material (mit Aufschlag) + Lohnkosten
                  const stundensatz = kalkSettings?.stundensatz_monteur || 52;
                  const aufschlag = kalkSettings?.aufschlag_material_prozent || 40;
                  const lohnKosten = pos.lohnzeit_minuten ? (pos.lohnzeit_minuten / 60) * stundensatz : 0;
                  const materialMitAufschlag = gpNetto ? gpNetto * (1 + aufschlag / 100) : 0;
                  const gpInklLohn = preamble ? null : (materialMitAufschlag + (lohnKosten * (pos.menge || 1)));
                  // Cu-Warnung: Kabel die noch keinen OMD-Tagespreis haben
                  const isCable = (pos.art_kurztext || '').match(/NYM|NYY|NYCWY|N2XH|NHXH/i);

                  // A1: Preamble/title rows get distinct styling
                  if (preamble) {
                    return (
                      <Table.Row
                        key={pos.id}
                        className="bg-gray-50 border-l-4 border-l-slate-300"
                      >
                        <Table.Cell className="text-gray-400 text-xs">
                          {pos.lfd_nr}
                        </Table.Cell>
                        <Table.Cell>
                          <span className="font-mono text-xs text-gray-400">{pos.oz || '-'}</span>
                        </Table.Cell>
                        <Table.Cell colSpan={5}>
                          <div className="flex items-center gap-2">
                            <PosKennZTag posKennZ={pos.pos_kenn_z} />
                            {(() => {
                              const kurztext = pos.art_kurztext || '';
                              const fullText = pos.art_langtext_full || pos.art_langtext || '';
                              const displayText = kurztext || truncateText(fullText, 60);
                              const hasLangtext = fullText.length > 50;
                              if (hasLangtext) {
                                return (
                                  <details className="text-sm text-gray-500 italic min-w-0">
                                    <summary className="cursor-pointer hover:text-gray-800">
                                      {displayText} <span className="text-xs text-blue-500 not-italic ml-1">[Langtext anzeigen]</span>
                                    </summary>
                                    <div className="mt-1 whitespace-pre-wrap leading-relaxed bg-white p-2 rounded border text-gray-700 not-italic text-xs max-h-48 overflow-y-auto">
                                      {fullText}
                                    </div>
                                  </details>
                                );
                              }
                              return <span className="text-sm text-gray-500 italic">{displayText}</span>;
                            })()}
                          </div>
                        </Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                    );
                  }

                  // Normal position row
                  return (
                    <Table.Row
                      key={pos.id}
                      clickable
                      onClick={() => handleOpenEdit(pos)}
                      className="hover:bg-gray-50"
                    >
                      <Table.Cell className="text-gray-500 text-sm">
                        {pos.lfd_nr}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="font-mono text-sm">{pos.oz || '-'}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-1">
                          {/* v2 Kategorie-Badge */}
                          {pos.position_kategorie && V2_KATEGORIE_BADGES[pos.position_kategorie] && (
                            <span
                              className={`inline-flex items-center justify-center shrink-0 w-5 h-5 rounded text-[10px] font-bold ${V2_KATEGORIE_BADGES[pos.position_kategorie].color}`}
                              title={V2_KATEGORIE_BADGES[pos.position_kategorie].fullLabel}
                            >
                              {V2_KATEGORIE_BADGES[pos.position_kategorie].label}
                            </span>
                          )}
                          {/* v1 Analyse-Badge (Fallback wenn kein v2) */}
                          {!pos.position_kategorie && pos.analysis_category && CATEGORY_BADGES[pos.analysis_category] && (
                            <span
                              className={`inline-flex items-center shrink-0 px-1 py-0.5 rounded text-[10px] font-medium ${CATEGORY_BADGES[pos.analysis_category].color}`}
                              title={CATEGORY_BADGES[pos.analysis_category].label}
                            >
                              {CATEGORY_BADGES[pos.analysis_category].label}
                            </span>
                          )}
                          <span
                            className="text-sm truncate"
                            title={pos.art_langtext_full || pos.art_langtext || pos.art_kurztext}
                          >
                            {pos.art_kurztext || truncateText(pos.art_langtext, 80)}
                          </span>
                          {pos.is_composite === 1 && (
                            <span className="shrink-0 w-2 h-2 rounded-full bg-purple-400" title="Sammelposition" />
                          )}
                          {pos.manuell_geprüft === 1 && (
                            <span className="shrink-0 text-xs" title="Manuell geprüft">✅</span>
                          )}
                          {pos.warnungen && JSON.parse(pos.warnungen || '[]').length > 0 && (
                            <span className="shrink-0 text-xs" title={JSON.parse(pos.warnungen).join('\n')}>⚠️</span>
                          )}
                          {detectAttentionHints(pos.art_langtext_full || pos.art_langtext || '').length > 0 && !pos.warnungen && (
                            <span className="shrink-0 text-xs" title={pos.attention_resolved ? 'Kleinteile bepreist' : 'Enthält Kleinteile / Zubehör'}>
                              {pos.attention_resolved ? '✅' : '⚠️'}
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        <span className="text-sm">
                          {pos.menge != null ? pos.menge : '-'}
                          {pos.mengeneinheit && (
                            <span className="text-gray-400 ml-1">{pos.mengeneinheit}</span>
                          )}
                        </span>
                      </Table.Cell>
                      {/* Lohnzeit */}
                      <Table.Cell className="text-right">
                        {pos.lohnzeit_minuten != null ? (
                          <span className="text-xs text-gray-600" title={pos.lohnzeit_hinweis || ''}>
                            {pos.lohnzeit_minuten}m
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        <span className="text-sm font-medium">
                          {pos.pr_netto != null ? formatCurrency(pos.pr_netto) : '-'}
                        </span>
                        {/* Kupferzuschlag-Warnung bei Kabeln ohne OMD-Preis */}
                        {isCable && pos.pr_netto > 0 && pos.pr_netto < 1 && (
                          <span className="text-orange-500 text-xs ml-1" title="Kabelpreis vermutlich ohne Kupferzuschlag — OMD-Preise laden!">⚠️Cu</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        <span className="text-sm font-medium">
                          {gpNetto != null ? formatCurrency(gpNetto) : '-'}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        <span className={`text-sm font-medium ${gpInklLohn > 0 ? 'text-indigo-700' : ''}`}>
                          {gpInklLohn != null && gpInklLohn > 0 ? formatCurrency(gpInklLohn) : '-'}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {pos.obeta_article_number ? (
                          <span className="font-mono text-xs text-blue-600">
                            {pos.obeta_article_number}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        <StatusBadge status={pos.match_status} />
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </Table.Loading>
        {filteredPositions.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 text-sm text-gray-500">
            {filteredPositions.length} von {positions.length} Positionen
          </div>
        )}
      </Card>

      {/* Position Edit Modal + OCI Sidebar */}
      {editModal.open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fadeIn" onClick={(e) => { if (e.target === e.currentTarget) handleSavePosition(true); }}>
        <div className="flex gap-3 max-w-7xl w-full mx-4 max-h-[92vh]">
          {/* Main Modal */}
          <div className="flex-1 min-w-0 bg-white rounded-xl shadow-2xl flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <button type="button" disabled={!prevPos} onClick={(e) => { e.stopPropagation(); prevPos && handleNavigate(prevPos); }} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Vorherige Position (←)"><ChevronLeft className="w-5 h-5" /></button>
                      <span className="text-xs text-gray-400 min-w-[4rem] text-center">{navIndex >= 0 ? `${navIndex + 1} / ${navigablePositions.length}` : ''}</span>
                      <button type="button" disabled={!nextPos} onClick={(e) => { e.stopPropagation(); nextPos && handleNavigate(nextPos); }} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" title="Nächste Position (→)"><ChevronRight className="w-5 h-5" /></button>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Position – {editModal.position?.oz || ''}</h2>
                    {editModal.position?.menge != null && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{editModal.position.menge} {editModal.position.mengeneinheit || 'Stk'}</span>
                    )}
                  </div>
                  {editModal.position?.art_langtext && (
                    <p className="text-sm text-gray-500 mt-1 truncate max-w-xl">{editModal.position.art_langtext}</p>
                  )}
                </div>
                <button onClick={() => handleSavePosition(true)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
        {editModal.position && (
          <div className="space-y-4">
            {/* ① LLM-Analyse (top) */}
            {editModal.position?.analysis_category && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-amber-800">LLM-Analyse</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CATEGORY_BADGES[editModal.position.analysis_category]?.color || ''}`}>
                    {CATEGORY_BADGES[editModal.position.analysis_category]?.label || editModal.position.analysis_category}
                  </span>
                  {editModal.position.analysis_is_product === 0 && (
                    <span className="text-[10px] text-gray-500">(kein Produkt)</span>
                  )}
                  {editModal.position.analysis_confidence != null && (
                    <span className="text-[10px] text-gray-400">{Math.round(editModal.position.analysis_confidence * 100)}%</span>
                  )}
                </div>
                {(() => {
                  let artNrFromFeatures = null;
                  try {
                    const f = JSON.parse(editModal.position.analysis_features || '{}');
                    artNrFromFeatures = f.articleNumber || null;
                  } catch {}
                  const searchVal = editModal.position.analysis_search;
                  return (
                    <>
                      {artNrFromFeatures && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-emerald-700">Artikelnr.:</span>
                          <button
                            type="button"
                            className="text-xs text-emerald-700 hover:underline font-mono font-semibold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200"
                            onClick={() => {
                              setModalDatanormQuery(artNrFromFeatures);
                              setModalDatanormMode('article');
                            }}
                          >
                            {artNrFromFeatures}
                          </button>
                        </div>
                      )}
                      {searchVal && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-amber-700">Suchvorschlag:</span>
                          <button
                            type="button"
                            className="text-xs text-blue-600 hover:underline font-mono"
                            onClick={() => {
                              setModalDatanormQuery(searchVal);
                              setModalDatanormMode(artNrFromFeatures && searchVal === artNrFromFeatures ? 'article' : 'fts');
                            }}
                          >
                            {searchVal}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
                {editModal.position.analysis_features && (
                  <div className="text-[10px] text-amber-600 mt-1">
                    {(() => {
                      try {
                        const f = JSON.parse(editModal.position.analysis_features);
                        return Object.entries(f).filter(([k,v]) => v && k !== 'articleNumber').map(([k,v]) => `${k}: ${v}`).join(' | ');
                      } catch { return ''; }
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Hinweis-Badges */}
            {attentionHints.length > 0 && (
              <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {attentionHints.map((hint, i) => (
                      <div key={i} className="text-xs text-red-700 font-medium py-0.5">⚠️ {hint}</div>
                    ))}
                  </div>
                  <label className="flex items-center gap-1.5 ml-3 shrink-0 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      checked={!!editForm.attention_resolved}
                      onChange={(e) => handleFormChange('attention_resolved', e.target.checked ? 1 : 0)}
                    />
                    <span className="text-xs text-gray-600 whitespace-nowrap">Kleinteile bepreist</span>
                  </label>
                </div>
              </div>
            )}

            {/* ② Datanorm-Suche + OCI Button */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Datanorm-Suche</label>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={handleAutoFillSearch}
                >
                  Aus Beschreibung suchen
                </button>
              </div>

              {/* Mode toggle tabs */}
              <div className="flex items-center gap-1 mb-2">
                {DATANORM_SEARCH_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      modalDatanormMode === mode.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      setModalDatanormMode(mode.value);
                      if (modalDatanormQuery.length >= 2) setModalDatanormResults([]);
                    }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input pl-10"
                    placeholder={
                      modalDatanormMode === 'article' ? 'Artikelnummer eingeben...'
                      : modalDatanormMode === 'ean' ? 'EAN/GTIN eingeben...'
                      : 'Suchbegriff eingeben...'
                    }
                    value={modalDatanormQuery}
                    onChange={(e) => setModalDatanormQuery(e.target.value)}
                  />
                  {modalDatanormSearching && (
                    <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
              </div>

              {/* Result cards */}
              {modalDatanormResults.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {modalDatanormResults.map((item, idx) => {
                    const artNr = item.articleNumber ?? item.article_number;
                    const text1 = item.shortText1 ?? item.short_text_1 ?? '';
                    const text2 = item.shortText2 ?? item.short_text_2 ?? '';
                    const netPrice = item.netPrice ?? item.net_price;
                    const priceBase = item.priceBase ?? item.price_base ?? 1;
                    const unit = item.unit || 'Stk';
                    const ean = item.ean || '';

                    return (
                      <div key={idx} className="px-4 py-3 hover:bg-blue-50 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-mono text-sm font-medium text-gray-900 select-all">{artNr}</span>
                              {ean && <span className="text-[10px] text-gray-400 font-mono">EAN: {ean}</span>}
                            </div>
                            <p className="text-sm font-medium text-gray-800 truncate">{text1}</p>
                            {text2 && <p className="text-xs text-gray-500 truncate">{text2}</p>}
                          </div>
                          <div className="shrink-0 text-right flex flex-col items-end gap-1">
                            <div>
                              <span className="text-sm font-bold text-gray-900">{formatCurrency(netPrice)}</span>
                              <span className="text-[10px] text-gray-400 ml-1">
                                / {priceBase > 1 ? `${priceBase} ` : ''}{unit}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                              onClick={() => handleApplyDatanorm(item)}
                            >
                              <Check className="w-3.5 h-3.5" />
                              Übernehmen
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {modalDatanormQuery.length >= 2 && !modalDatanormSearching && modalDatanormResults.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">Keine Ergebnisse gefunden</p>
              )}

            </div>

            {/* ③ Zusammenfassung (EP-Aufschlüsselung + Lohnzeit + Geprüft) */}
            {(() => {
              const compositeTotal = compositeItems.reduce((sum, ci) => sum + (ci.pr_netto * ci.quantity / (ci.pr_basis > 1 ? ci.pr_basis : 1)), 0);
              const materialEK = compositeTotal > 0 ? compositeTotal : (parseFloat(editForm.pr_netto) || 0);
              // FEATURE-001: Werte aus Kalkulations-Einstellungen statt hardcoded
              const aufschlagProzent = kalkSettings?.aufschlag_material_prozent ?? 40;
              const materialAufschlag = materialEK * (aufschlagProzent / 100);
              const lohnMinuten = parseFloat(editForm.lohnzeit_minuten) || 0;
              const stundensatz = kalkSettings?.stundensatz_monteur ?? 52;
              const lohnKosten = (lohnMinuten / 60) * stundensatz;
              // FEATURE-001: BGK-Anteil pro Position (proportional nach Material-EK)
              const bgkGesamt = kalkSettings?.bgk_gesamt ?? 0;
              const bgkAnteil = 0; // BGK wird erst in Gesamtübersicht verteilt
              const epNetto = materialEK + materialAufschlag + lohnKosten + bgkAnteil;
              const gpNetto = calculateGP(editModal.position.menge, epNetto, 1);
              const statusCfg = STATUS_CONFIG[editForm.match_status] || STATUS_CONFIG.pending;
              const kat = editModal.position.position_kategorie;
              const katBadge = kat && V2_KATEGORIE_BADGES[kat];
              return (
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  {/* EP-Aufschlüsselung */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-4">
                      {/* Kategorie-Badge */}
                      {katBadge && (
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-bold ${katBadge.color}`} title={katBadge.fullLabel}>
                          {katBadge.label}
                        </span>
                      )}
                      {/* EP-Details */}
                      <div className="flex items-center gap-4">
                        <div>
                          <span className="text-[10px] text-gray-400">Material EK</span>
                          <div className="text-sm font-medium text-gray-700">{formatCurrency(materialEK)}</div>
                        </div>
                        <div className="text-gray-300">+</div>
                        <div>
                          <span className="text-[10px] text-gray-400">+{aufschlagProzent}%</span>
                          <div className="text-sm font-medium text-blue-600">{formatCurrency(materialAufschlag)}</div>
                        </div>
                        <div className="text-gray-300">+</div>
                        <div>
                          <span className="text-[10px] text-gray-400">Lohn</span>
                          <div className="text-sm font-medium text-orange-600">{formatCurrency(lohnKosten)}</div>
                        </div>
                        <div className="text-gray-300">=</div>
                        <div>
                          <span className="text-[10px] text-gray-400">EP Netto</span>
                          <div className="text-lg font-bold text-gray-900">{formatCurrency(epNetto)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {editModal.position.menge != null && (
                        <div className="text-right">
                          <span className="text-[10px] text-gray-400">GP ({editModal.position.menge} {editModal.position.mengeneinheit || 'Stk'})</span>
                          <div className="text-lg font-bold text-gray-900">{formatCurrency(gpNetto)}</div>
                        </div>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                        {statusCfg.label}
                      </span>
                    </div>
                  </div>

                  {/* Lohnzeit + Geprüft + Match-Info */}
                  <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                    {/* Lohnzeit-Editor */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-gray-400">Lohnzeit:</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={editForm.lohnzeit_minuten ?? ''}
                        onChange={(e) => handleFormChange('lohnzeit_minuten', e.target.value === '' ? null : parseFloat(e.target.value))}
                        className="w-14 text-xs text-center border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="-"
                      />
                      <span className="text-[10px] text-gray-400">min/{editModal.position.mengeneinheit || 'Stk'}</span>
                      {editModal.position.lohnzeit_hinweis && (
                        <span className="text-[10px] text-gray-400" title={editModal.position.lohnzeit_hinweis}>💡</span>
                      )}
                    </div>

                    <div className="h-4 w-px bg-gray-200" />

                    {/* Manuell geprüft Checkbox */}
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!editForm.manuell_geprüft}
                        onChange={(e) => handleFormChange('manuell_geprüft', e.target.checked ? 1 : 0)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] text-gray-500">Geprüft</span>
                    </label>

                    <div className="flex-1" />

                    {/* Match + OMD info */}
                    {editModal.position.match_source && (
                      <span className="text-[10px] text-gray-400">
                        {editModal.position.match_source}
                        {editModal.position.match_confidence != null && ` (${Math.round(editModal.position.match_confidence * 100)}%)`}
                      </span>
                    )}
                    {editModal.position.omd_net_price != null && (
                      <span className="text-[10px] text-green-600">
                        OMD: {formatCurrency(editModal.position.omd_net_price)}
                        {editModal.position.omd_metal_surcharge != null && ` +${formatCurrency(editModal.position.omd_metal_surcharge)} Cu`}
                      </span>
                    )}
                  </div>

                  {/* Warnungen (aus v2 Analyse) */}
                  {editModal.position.warnungen && (() => {
                    try {
                      const warns = JSON.parse(editModal.position.warnungen);
                      return warns.length > 0 ? (
                        <div className="mt-2 pt-2 border-t border-amber-200 space-y-1">
                          {warns.map((w, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>{w}</span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    } catch { return null; }
                  })()}
                </div>
              );
            })()}

            {/* ⑤ Artikel (immer sichtbar) */}
            {!isPreambleRow(editModal.position) && (
              <div className="border border-purple-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-purple-50 border-b border-purple-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">
                      Artikel ({compositeItems.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {compositeItems.length > 0 && (
                      <>
                        <span className="text-xs text-green-600 font-medium">
                          Summe: {formatCurrency(compositeItems.reduce((sum, ci) => sum + (ci.pr_netto * ci.quantity / (ci.pr_basis > 1 ? ci.pr_basis : 1)), 0))}
                        </span>
                        <button
                          className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800"
                          onClick={handleCopyAllCompositeItems}
                          title="Alle Artikel ins Clipboard kopieren"
                        >
                          <Clipboard className="w-3 h-3" />
                          Alle kopieren
                        </button>
                        <button
                          className="text-xs text-purple-500 hover:text-purple-700 underline"
                          onClick={() => handleExtractComposite(true)}
                          disabled={extracting}
                        >
                          {extracting ? 'Analysiert...' : 'Erneut analysieren'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  {compositeLoading ? (
                    <div className="p-4 text-center text-sm text-gray-500">Lade Artikel...</div>
                  ) : compositeItems.length === 0 ? (
                    <div className="p-4 text-center space-y-3">
                      <p className="text-sm text-gray-500">Noch keine Artikel zugewiesen</p>
                      {clipboardItems.length > 0 && (
                        <div className="flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <Clipboard className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                          <span className="text-xs text-amber-700">{clipboardItems.length} Artikel im Clipboard</span>
                          <button
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
                            onClick={handlePasteClipboard}
                          >
                            Einfügen
                          </button>
                          <button
                            className="p-0.5 text-amber-400 hover:text-amber-600"
                            onClick={() => setClipboardItems([])}
                            title="Clipboard leeren"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-center gap-3">
                        <button
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
                          onClick={() => handleExtractComposite(true)}
                          disabled={extracting}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {extracting ? 'Analysiert...' : 'Per KI extrahieren'}
                        </button>
                        <button
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-purple-300 text-purple-700 text-xs font-medium rounded-lg hover:bg-purple-50"
                          onClick={handleAddCompositeItem}
                        >
                          + Manuell hinzufügen
                        </button>
                        <button
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-50"
                          onClick={() => { setShowTemplateList(!showTemplateList); loadTemplates(editModal.position?.analysis_category); }}
                        >
                          <ClipboardPaste className="w-3.5 h-3.5" />
                          Vorlage anwenden
                        </button>
                      </div>
                      {/* Template list dropdown */}
                      {showTemplateList && (
                        <div className="border border-amber-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                          {templates.length === 0 ? (
                            <div className="p-3 text-xs text-gray-400 text-center">Keine Vorlagen vorhanden</div>
                          ) : templates.map(t => (
                            <div key={t.id} className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 border-b border-amber-100 last:border-b-0">
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-700">{t.name}</div>
                                <div className="text-[10px] text-gray-400">{t.items?.length || 0} Artikel {t.category && `· ${t.category}`} {t.usage_count > 0 && `· ${t.usage_count}× verwendet`}</div>
                              </div>
                              <button
                                className="text-xs text-amber-700 hover:text-amber-900 font-medium px-2 py-1 rounded hover:bg-amber-100 disabled:opacity-50"
                                onClick={() => handleApplyTemplate(t.id)}
                                disabled={applyingTemplate}
                              >
                                {applyingTemplate ? '...' : 'Anwenden'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {clipboardItems.length > 0 && (
                        <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-b border-amber-200">
                          <Clipboard className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                          <span className="text-xs text-amber-700 truncate">
                            {clipboardItems.length} im Clipboard: {clipboardItems.map(c => c.obeta_article_number || c.description?.slice(0, 20)).join(', ')}
                          </span>
                          <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                            <button
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
                              onClick={handlePasteClipboard}
                            >
                              Einfügen
                            </button>
                            <button
                              className="p-0.5 text-amber-400 hover:text-amber-600"
                              onClick={() => setClipboardItems([])}
                              title="Clipboard leeren"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                      {compositeItems.map((ci) => (
                        <CompositeItemRow
                          key={ci.id}
                          item={ci}
                          saving={savingCompositeId === ci.id}
                          onSave={handleSaveCompositeItem}
                          onDelete={handleDeleteCompositeItem}
                          onVerifySingle={handleVerifySingleItem}
                          onCopy={handleCopyCompositeItem}
                        />
                      ))}
                      <div className="px-4 py-2 bg-purple-50/50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                            onClick={handleAddCompositeItem}
                          >
                            + Artikel hinzufügen
                          </button>
                          <button
                            className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium"
                            onClick={() => { setShowTemplateList(!showTemplateList); loadTemplates(editModal.position?.analysis_category); }}
                          >
                            <ClipboardPaste className="w-3 h-3" />
                            Vorlage anwenden
                          </button>
                        </div>
                        {compositeItems.filter(ci => ci.pr_netto > 0).length >= 2 && (
                          <div>
                            {showTemplateSave ? (
                              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                <input
                                  type="text"
                                  className="input text-xs py-1 px-2 w-40"
                                  placeholder="Vorlagenname..."
                                  value={templateName}
                                  onChange={e => setTemplateName(e.target.value)}
                                  autoFocus
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveTemplate(); if (e.key === 'Escape') setShowTemplateSave(false); }}
                                />
                                <button
                                  className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                  onClick={handleSaveTemplate}
                                  disabled={savingTemplate || !templateName.trim()}
                                  title="Speichern"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button className="p-1 text-gray-400 hover:text-gray-600" onClick={() => setShowTemplateSave(false)}>
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
                                onClick={() => { setShowTemplateSave(true); setTemplateName(''); setTemplateCategory(editModal.position?.analysis_category || ''); }}
                              >
                                <BookmarkPlus className="w-3 h-3" />
                                Als Vorlage speichern
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Template list dropdown (when items exist) */}
                      {showTemplateList && (
                        <div className="border-t border-amber-200 max-h-40 overflow-y-auto">
                          {templates.length === 0 ? (
                            <div className="p-3 text-xs text-gray-400 text-center">Keine Vorlagen vorhanden</div>
                          ) : templates.map(t => (
                            <div key={t.id} className="flex items-center justify-between px-4 py-2 hover:bg-amber-50 border-b border-amber-100 last:border-b-0">
                              <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-700">{t.name}</div>
                                <div className="text-[10px] text-gray-400">{t.items?.length || 0} Artikel {t.usage_count > 0 && `· ${t.usage_count}× verwendet`}</div>
                              </div>
                              <button
                                className="text-xs text-amber-700 hover:text-amber-900 font-medium px-2 py-1 rounded hover:bg-amber-100 disabled:opacity-50"
                                onClick={() => handleApplyTemplate(t.id)}
                                disabled={applyingTemplate}
                              >
                                {applyingTemplate ? '...' : 'Anwenden'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Notizen */}
            <div>
              <label className="label">Notizen</label>
              <textarea
                className="input min-h-[40px] resize-none text-sm"
                value={editForm.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Anmerkungen..."
              />
            </div>

            {/* Verify: Position geprüft — in Katalog übernehmen */}
            {editModal.position && hasArticlesForVerify && (
              <div className="px-4 py-3 border border-emerald-200 rounded-lg bg-emerald-50/50">
                {editForm.match_status === 'verified' ? (
                  <div className="flex items-center justify-between text-sm text-emerald-700">
                    <span className="flex items-center gap-1.5">
                      <Check className="w-4 h-4" />
                      Geprüft ({compositeItems.length > 0 ? compositeItems.filter(ci => ci.obeta_article_number).length : 1} Artikel im Katalog)
                    </span>
                    <button
                      className="text-xs text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-emerald-100"
                      onClick={handleVerifyPosition}
                      title="Erneut in Katalog übernehmen"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    className="w-full py-2 px-4 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 font-medium text-sm flex items-center justify-center gap-2"
                    onClick={handleVerifyPosition}
                  >
                    <Check className="w-4 h-4" />
                    Position geprüft — in Katalog übernehmen
                  </button>
                )}
              </div>
            )}

            {/* ⑥ Langtext (aufklappbar, standardmäßig ZU) */}
            <details className="border border-gray-200 rounded-lg overflow-hidden">
              <summary className="px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer text-xs font-medium text-gray-600 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                Langtext
                {editModal.position.is_composite === 1 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">Sammelposition</span>
                )}
              </summary>
              <div className="px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap max-h-60 overflow-y-auto leading-relaxed">
                {(editModal.position.art_langtext_full || editModal.position.art_langtext || '-')
                  .replace(/[ \t]{2,}/g, ' ')
                  .replace(/\n{3,}/g, '\n\n')
                  .replace(/^\s+$/gm, '')
                  .replace(/:\s{2,}/g, ': ')
                  .trim()}
              </div>
            </details>

            {/* ⑦ Vortext/Kontext — volle OZ-Hierarchie (übergeordnet zuerst, spezifisch darunter) */}
            {contextPreambles.length > 0 && (
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-blue-50 text-xs font-medium text-blue-700 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  Vortext / Kontext ({contextPreambles.length} {contextPreambles.length === 1 ? 'Ebene' : 'Ebenen'})
                </div>
                {contextPreambles.map((preamble, idx) => {
                  const fullText = preamble.art_langtext_full || preamble.art_langtext || '';
                  return (
                    <details key={preamble.id} open={idx === 0}>
                      <summary className="px-3 py-1.5 text-xs cursor-pointer hover:bg-blue-50 border-t border-blue-100">
                        <span className="font-mono text-blue-400 mr-2">{preamble.oz}</span>
                        <PosKennZTag posKennZ={preamble.pos_kenn_z} />
                        <span className="ml-1 text-gray-600">{(preamble.art_langtext || '').substring(0, 80)}{(preamble.art_langtext || '').length > 80 ? '...' : ''}</span>
                      </summary>
                      <div className="px-3 py-2 text-xs text-blue-800 bg-blue-50/50 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
                        {fullText.replace(/\n{3,}/g, '\n\n')}
                      </div>
                    </details>
                  );
                })}
              </div>
            )}
          </div>
        )}
            </div>
            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between shrink-0">
              <span className="text-xs">
                {saveStatus === 'saved' && <span className="text-green-600">&#10003; Gespeichert</span>}
                {saveStatus === 'saving' && <span className="text-blue-500">&#9203; Speichert...</span>}
                {saveStatus === 'dirty' && <span className="text-orange-500">&#9679; Ungespeichert</span>}
                {saveStatus === 'error' && <span className="text-red-500">&#10005; Fehler</span>}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleSavePosition(true)} loading={saving}>Schließen</Button>
              </div>
            </div>
          </div>

          {/* OCI + Catalog Sidebar */}
          <div className="w-72 bg-white rounded-xl shadow-2xl flex flex-col max-h-[92vh] shrink-0">
            {/* Tab Header */}
            <div className="flex border-b border-gray-200">
              <button
                className={`flex-1 px-3 py-2 text-xs font-medium ${sidebarTab === 'oci' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setSidebarTab('oci')}
              >
                OCI ({ociSidebarItems.length})
              </button>
              <button
                className={`flex-1 px-3 py-2 text-xs font-medium ${sidebarTab === 'catalog' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setSidebarTab('catalog')}
              >
                Katalog
              </button>
            </div>

            {/* Target indicator */}
            <div className="px-3 pt-2 text-[10px] text-gray-400">
              Klick auf → fügt neuen Einzelartikel hinzu
            </div>

            {/* OCI Tab */}
            {sidebarTab === 'oci' && (
              <>
                <div className="px-3 py-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-700">OCI-Artikel</span>
                    <button onClick={loadOciSidebarItems} className="p-1 hover:bg-gray-100 rounded" title="Aktualisieren">
                      <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${ociSidebarLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  <input
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Filter..."
                    value={ociSidebarFilter}
                    onChange={e => setOciSidebarFilter(e.target.value)}
                  />
                </div>
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                  {ociSidebarItems
                    .filter(item => {
                      if (!ociSidebarFilter) return true;
                      const q = ociSidebarFilter.toLowerCase();
                      return (item.description || '').toLowerCase().includes(q)
                        || (item.vendorMat || '').toLowerCase().includes(q);
                    })
                    .map((item, idx) => (
                    <div key={`${item.callbackId}-${idx}`} className="px-3 py-2 hover:bg-blue-50 transition-colors group">
                      <div className="flex items-start gap-1.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 truncate" title={item.description}>{item.description}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-mono font-medium text-blue-600">{item.vendorMat}</span>
                            {item.price > 0 && (
                              <span className="text-[10px] text-gray-400">{formatCurrency(item.price)}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100">
                          <button
                            className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
                            onClick={() => handleApplyOciToField(item)}
                            title="Artikelnummer übernehmen"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                          <button
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                            onClick={() => handleDeleteOciCallback(item.callbackId)}
                            title="Callback entfernen"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {ociSidebarItems.length === 0 && !ociSidebarLoading && (
                    <div className="px-3 py-8 text-center text-xs text-gray-400">
                      <Package className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                      Keine OCI-Artikel vorhanden.<br />Importiere zuerst über den Produktkatalog.
                    </div>
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-gray-200">
                  <button
                    className="w-full text-xs text-orange-600 hover:text-orange-800 font-medium py-1.5 border border-orange-200 rounded-md hover:bg-orange-50 transition-colors flex items-center justify-center gap-1.5"
                    onClick={() => window.open('/api/oci/launch/obeta', 'oci_import', 'width=1200,height=800,scrollbars=yes,resizable=yes')}
                  >
                    <ExternalLink className="w-3 h-3" />
                    OCI Import öffnen
                  </button>
                </div>
              </>
            )}

            {/* Catalog Tab */}
            {sidebarTab === 'catalog' && (
              <>
                <div className="px-3 py-2">
                  <input
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Artikel suchen..."
                    value={catalogFilter}
                    onChange={e => setCatalogFilter(e.target.value)}
                  />
                </div>
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                  {catalogLoading ? (
                    <div className="px-3 py-8 text-center text-xs text-gray-400">Laden...</div>
                  ) : catalogProducts.length === 0 ? (
                    <div className="px-3 py-8 text-center text-xs text-gray-400">
                      <Package className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                      {catalogFilter ? 'Keine Artikel gefunden.' : 'Keine Artikel im Katalog.'}
                    </div>
                  ) : (
                    catalogProducts.map(product => (
                      <div key={product.id} className="px-3 py-2 hover:bg-blue-50 transition-colors group">
                        <div className="flex items-start gap-1.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-700 truncate" title={product.name}>{product.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs font-mono font-medium text-blue-600">{product.article_number || product.articleNumber}</span>
                              {(product.price || 0) > 0 && (
                                <span className="text-[10px] text-gray-400">{formatCurrency(product.price)}</span>
                              )}
                            </div>
                          </div>
                          <button
                            className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded shrink-0 opacity-60 group-hover:opacity-100"
                            onClick={() => handleApplyOciToField(catalogItemToOci(product))}
                            title="Artikelnummer übernehmen"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* P94 Import Modal */}
      <Modal
        isOpen={importModal}
        onClose={() => {
          setImportModal(false);
          setImportFile(null);
          setImportResult(null);
        }}
        title="P94 Import"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setImportModal(false);
                setImportFile(null);
                setImportResult(null);
              }}
            >
              {importResult ? 'Schließen' : 'Abbrechen'}
            </Button>
            {!importResult && (
              <Button
                variant="primary"
                icon={Upload}
                onClick={handleImportP94}
                loading={importing}
                disabled={!importFile}
              >
                Importieren
              </Button>
            )}
          </>
        }
      >
        {!importResult ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Laden Sie eine P94-Datei hoch, um Preise von Lieferanten zu importieren.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <label className="cursor-pointer">
                <span className="text-primary-600 hover:text-primary-700 font-medium">
                  Datei auswählen
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".p94,.P94"
                  onChange={(e) => setImportFile(e.target.files[0] || null)}
                />
              </label>
              {importFile && (
                <p className="mt-2 text-sm text-gray-700 font-medium">
                  {importFile.name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Import abgeschlossen:</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">
                  {importResult.merged || 0}
                </p>
                <p className="text-sm text-green-600">Zusammengeführt</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">
                  {importResult.conflict || 0}
                </p>
                <p className="text-sm text-yellow-600">Konflikte</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">
                  {importResult.notFound || 0}
                </p>
                <p className="text-sm text-red-600">Nicht gefunden</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KalkulationDetail;
